<template>
    <div id="prompt-detail" class="standard-mode">
        <v-container fluid class="prompt-detail-container">
            <!-- Header -->
            <div class="prompt-detail-header">
                <div class="prompt-detail-header__left">
                    <v-btn icon small class="prompt-detail-back" @click="goBack" aria-label="Back">
                        <v-icon small>{{ embedded ? "mdi-close" : "mdi-arrow-left" }}</v-icon>
                    </v-btn>
                    <div class="prompt-detail-breadcrumb">
                        <span class="prompt-detail-breadcrumb__crumb">{{ project ? project.title : "—" }}</span>
                        <v-icon x-small class="prompt-detail-breadcrumb__sep">mdi-chevron-right</v-icon>
                        <span class="prompt-detail-breadcrumb__crumb">Prompts</span>
                        <v-icon x-small class="prompt-detail-breadcrumb__sep">mdi-chevron-right</v-icon>
                        <span class="prompt-detail-breadcrumb__current">{{ promptTitle || "Prompt Detail" }}</span>
                    </div>
                </div>
            </div>

            <!-- Error -->
            <v-alert v-if="error" type="error" dense outlined class="mb-3">
                {{ error }}
            </v-alert>
            <v-row>
                <!-- Header: Prompt + KPIs + Filter -->

                <!-- Neue KPI-Cards (im AIProject-Metrics-Card-Stil) -->
                <v-row dense class="brand-metrics-cards-wrapper">
                    <v-col cols="12" sm="6" md="4">
                        <v-card class="brand-metric-card prompt-info-card quaroshadow rounded_card" outlined style="border-top: 4px solid var(--v-primary-base) !important; height: 100%">
                            <div class="pc-header">
                                <div class="pc-logo">
                                    <v-icon small color="primary">mdi-message-text-outline</v-icon>
                                </div>
                                <div class="pc-title-wrap">
                                    <h2 class="pc-title">Prompt</h2>
                                    <small class="pc-status">{{ projectBrandName }}</small>
                                </div>
                            </div>

                            <div class="prompt-info-title" :title="promptTitle || 'Untitled Prompt'">
                                {{ promptTitle || "Untitled Prompt" }}
                            </div>
                            <div class="prompt-info-tags" v-if="promptInfo && promptInfo.tags && promptInfo.tags.length">
                                <v-chip v-for="t in promptInfo.tags.slice(0, 4)" :key="t" small outlined class="ma-1 prompt-info-tag">
                                    {{ t }}
                                </v-chip>
                                <span v-if="promptInfo.tags.length > 4" class="caption grey--text text--darken-1 ml-1">+{{ promptInfo.tags.length - 4 }}</span>
                            </div>
                        </v-card>
                    </v-col>

                    <v-col cols="12" sm="6" md="4">
                        <v-card
                            class="brand-metric-card quaroshadow rounded_card"
                            outlined
                            style="border-top: 4px solid var(--v-primary-base) !important; height: 100%"
                            :class="{
                                'brand-metric-card--up': brandVisibleAll === true,
                                'brand-metric-card--down': brandVisibleAll === false
                            }">
                            <div class="pc-header">
                                <div class="pc-logo" style="font-size: 11px; font-weight: 600; overflow: hidden">
                                    <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="projectBrandName" style="border-radius: 10px" />
                                    <span v-else>{{ (projectBrandName || "BR").substring(0, 2).toUpperCase() }}</span>
                                </div>
                                <div class="pc-title-wrap">
                                    <h2 class="pc-title">Brand Visibility</h2>
                                    <small class="pc-status">{{ projectBrandName }}</small>
                                </div>
                            </div>

                            <div class="pc-body">
                                <div class="pc-metric">
                                    <div class="pc-metric-label">Platforms</div>
                                    <div class="pc-platform-icons">
                                        <div v-for="p in brandVisibilityPlatformsCurrent" :key="p.platform" class="pc-platform-icon" :class="{ 'is-visible': p.visible, 'is-hidden': !p.visible }">
                                            <span v-if="getPlatformInfo(p.platform).logo" class="pc-platform-icon-logo" v-html="getPlatformInfo(p.platform).logo" />
                                            <span v-else class="pc-platform-icon-fallback">
                                                {{ (p.platform || "?").substring(0, 2).toUpperCase() }}
                                            </span>
                                        </div>
                                    </div>
                                    <!-- <div class="pc-metric-sub" v-if="brandVisibilityCurrent">
                                        {{ brandVisibilityCountText }}
                                    </div> -->
                                </div>

                                <div class="pc-side">
                                    <div class="pc-diff-label">STATUS</div>

                                    <div class="pc-delta-badge" v-if="brandVisibleAll != null" :class="{ up: brandVisibleAll === true, down: brandVisibleAll === false }">
                                        <v-icon x-small class="mr-1" :color="brandVisibleAll === true ? 'white' : brandVisibleAll === false ? 'white' : 'grey'">
                                            {{ brandVisibleAll === true ? "mdi-check" : "mdi-alert" }}
                                        </v-icon>
                                        {{ brandVisibilityBadgeText }}
                                    </div>

                                    <div v-else class="pc-delta-badge na">—</div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                        <v-card
                            class="brand-metric-card quaroshadow rounded_card"
                            outlined
                            style="border-top: 4px solid var(--v-primary-base) !important; height: 100%"
                            :class="{
                                'brand-metric-card--up': brandSentimentDelta > 0,
                                'brand-metric-card--down': brandSentimentDelta < 0
                            }">
                            <div class="pc-header">
                                <div class="pc-logo" style="font-size: 11px; font-weight: 600; overflow: hidden">
                                    <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="projectBrandName" style="border-radius: 10px" />
                                    <span v-else>{{ (projectBrandName || "BS").substring(0, 2).toUpperCase() }}</span>
                                </div>
                                <div class="pc-title-wrap">
                                    <h2 class="pc-title">Sentiment</h2>
                                    <small class="pc-status">{{ projectBrandName }}</small>
                                </div>
                            </div>

                            <div class="pc-body">
                                <div class="pc-metric pc-metric--compact">
                                    <div class="pc-metric-label">Current</div>
                                    <div class="pc-value">{{ brandSentimentDisplayValue }}</div>

                                    <div v-if="brandSentimentCurrent != null" class="pc-sentiment-meter pc-sentiment-meter--compact">
                                        <div class="pc-sentiment-meter__bar">
                                            <div class="pc-sentiment-meter__zero" />
                                            <div class="pc-sentiment-meter__marker" :style="{ left: sentimentMarkerLeft(brandSentimentCurrent) }" />
                                        </div>
                                        <div class="pc-sentiment-meter__legend"><span>-1</span><span>0</span><span>+1</span></div>
                                    </div>
                                    <div v-else class="grey--text text--darken-1 text-caption mt-1">No sentiment data</div>
                                </div>

                                <div class="pc-side">
                                    <div class="pc-diff-label">CHANGE</div>

                                    <div class="pc-delta-badge" v-if="brandSentimentDelta != null" :class="{ up: brandSentimentDelta > 0, down: brandSentimentDelta < 0 }">
                                        <v-icon x-small class="mr-1" :color="brandSentimentDelta > 0 ? 'white' : brandSentimentDelta < 0 ? 'white' : 'grey'">
                                            {{ brandSentimentDelta > 0 ? "mdi-arrow-up" : brandSentimentDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}
                                        </v-icon>
                                        {{ brandSentimentDeltaText }}
                                    </div>

                                    <div v-else class="pc-delta-badge na">—</div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </v-row>
            <!-- Filter -->
            <v-row>
                <v-card class="mb-4 mt-4 pa-0 rounded_card quaroshadow prompt-response-card">
                    <div class="prompt-response-card__head">
                        <div class="prompt-response-card__head-left">
                            <div class="prompt-response-card__icon">
                                <v-icon small color="primary">mdi-message-reply-text-outline</v-icon>
                            </div>
                            <div>
                                <div class="prompt-response-card__title">Prompt Response</div>
                                <div class="prompt-response-card__subtitle">{{ selectedDateText || "Select a date" }}</div>
                            </div>
                        </div>
                        <v-menu v-model="datepickerMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto" aria-label="Select date">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    dense
                                    outlined
                                    hide-details
                                    class="prompt-response-card__date-input"
                                    label="Date"
                                    v-model="selectedDateText"
                                    rounded
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    aria-haspopup="dialog"
                                    aria-controls="date-picker"
                                    aria-label="Select date"
                                    v-bind="attrs"
                                    v-on="on" />
                            </template>
                            <v-date-picker id="date-picker" color="primary" :allowed-dates="allowedDates" show-current="false" v-model="selectedDate" @input="onDateChange" aria-label="Pick date" />
                        </v-menu>
                    </div>
                    <v-toolbar dense flat class="prompt-response-card__tabs-toolbar">
                        <v-tabs v-if="recentPlatforms.length" v-model="activeLatestPlatform" background-color="transparent" show-arrows hide-slider>
                            <v-tab v-for="(p, idx) in recentPlatforms" :key="p" :class="['la-platform-tab-btn', { 'la-platform-tab-btn--active': activeLatestPlatform === idx }]">
                                <span class="la-platform-tab">
                                    <span v-if="getPlatformInfo(p).logo" class="la-platform-logo" v-html="getPlatformInfo(p).logo"></span>
                                    <span>{{ getPlatformInfo(p).label || p || "–" }}</span>
                                </span>
                            </v-tab>
                        </v-tabs>
                    </v-toolbar>

                    <v-divider />

                    <div v-if="latestResponseForActivePlatform" class="la-container">
                        <div class="la-chat-area">
                            <div class="la-bubble la-ki-message">
                                <div class="la-meta">
                                    <span>{{ latestResponseForActivePlatform.platform || "–" }}</span>
                                    <span>· {{ formatDateTime(latestResponseForActivePlatform.importDate) }}</span>
                                    <span v-if="latestResponseForActivePlatform.tokenUsage != null"> · Tokens: {{ Math.round(latestResponseForActivePlatform.tokenUsage) }} </span>
                                </div>

                                <!-- Shopping-Karussell (ChatGPT chat_gpt_products) der Response -->
                                <div v-if="shoppingItemsForActiveResponse.length" class="la-shopping">
                                    <div class="la-shopping__label">
                                        <v-icon x-small class="mr-1" color="primary">mdi-cart-outline</v-icon>
                                        Shopping results ({{ shoppingItemsForActiveResponse.length }})
                                    </div>
                                    <!-- Interne Katalog-Queries, mit denen das Modell die Produkte gesucht hat -->
                                    <div v-if="shoppingQueriesForActiveResponse.length" class="la-queries la-queries--inline">
                                        <v-tooltip top max-width="320">
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="la-queries__label" v-bind="attrs" v-on="on">
                                                    <v-icon x-small class="mr-1" color="primary">mdi-magnify</v-icon>
                                                    Shopping queries ({{ shoppingQueriesForActiveResponse.length }})
                                                </span>
                                            </template>
                                            <span>Internal catalog search queries the model actually used to fetch these products</span>
                                        </v-tooltip>
                                        <span v-for="q in shoppingQueriesForActiveResponse" :key="`sq-${q}`" class="la-query-chip">{{ q }}</span>
                                    </div>
                                    <div class="la-shopping__scroller">
                                        <div v-for="item in shoppingItemsForActiveResponse" :key="`${item.rank}-${item.title}`" class="la-shopping-card">
                                            <div class="la-shopping-card__rank" :class="{ 'is-buybox': item.rank === 1 }">#{{ item.rank }}</div>
                                            <div class="la-shopping-card__img-wrap">
                                                <img v-if="item.image" :src="item.image" alt="" loading="lazy" />
                                                <v-icon v-else color="grey lighten-1">mdi-package-variant-closed</v-icon>
                                            </div>
                                            <div class="la-shopping-card__title" :title="item.title">{{ item.title }}</div>
                                            <div class="la-shopping-card__merchant">
                                                {{ item.merchant_label || item.merchant || "–" }}<span v-if="item.has_more_merchants" class="la-shopping-card__more"> + more</span>
                                            </div>
                                            <div class="la-shopping-card__meta">
                                                <span v-if="item.price != null" class="la-shopping-card__price">{{ formatShoppingPrice(item) }}</span>
                                                <span v-if="item.rating_value != null" class="la-shopping-card__rating">
                                                    <v-icon x-small color="amber">mdi-star</v-icon>
                                                    {{ Number(item.rating_value).toFixed(1) }}
                                                    <span v-if="item.rating_votes" class="la-shopping-card__votes">({{ Number(item.rating_votes).toLocaleString("de-DE") }})</span>
                                                </span>
                                            </div>
                                            <v-tooltip top max-width="320" :disabled="!item.shopping_query">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <div v-if="item.shopping_query" v-bind="attrs" v-on="on" class="la-shopping-card__query">„{{ item.shopping_query }}"</div>
                                                </template>
                                                <span>Internal shopping query ChatGPT used to fetch this product</span>
                                            </v-tooltip>
                                        </div>
                                    </div>
                                </div>

                                <template v-if="aiModePayload.results.length">
                                    <div v-if="aiModePayload.introHtml" class="la-text la-markdown la-aimode-intro" v-html="aiModePayload.introHtml"></div>
                                    <div class="la-aimode-list">
                                        <a
                                            v-for="(r, idx) in aiModePayload.results"
                                            :key="`${r.url}-${idx}`"
                                            class="la-aimode-card"
                                            :href="r.url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            :title="r.url"
                                        >
                                            <div class="la-aimode-card__head">
                                                <img
                                                    class="la-aimode-card__img"
                                                    :src="r.image || faviconUrl(r.domain, 64)"
                                                    :alt="r.name || r.domain"
                                                    loading="lazy"
                                                    referrerpolicy="no-referrer"
                                                    @error="onAiModeImgError"
                                                />
                                                <div class="la-aimode-card__ident">
                                                    <div class="la-aimode-card__name">{{ r.name || r.domain }}</div>
                                                    <div class="la-aimode-card__domain">{{ r.domain }}</div>
                                                </div>
                                                <v-icon x-small class="ml-auto" color="grey">mdi-open-in-new</v-icon>
                                            </div>
                                            <div v-if="r.html" class="la-aimode-card__text" v-html="r.html"></div>
                                        </a>
                                    </div>
                                </template>
                                <div v-else class="la-text la-markdown" v-html="renderedResponsePayload.html"></div>

                                <div v-if="perplexityPayload.marker" class="la-ppx-marker">
                                    <a
                                        v-if="perplexityMarkerSource"
                                        class="la-ppx-marker__chip"
                                        :href="perplexityMarkerSource.href"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        :title="perplexityMarkerSource.href"
                                    >
                                        <img class="fav" :src="faviconUrl(perplexityMarkerSource.domain, 32)" :alt="perplexityMarkerSource.domain" referrerpolicy="no-referrer" @error="onAiModeImgError" />
                                        <span>{{ perplexityMarkerSource.domain }}</span>
                                    </a>
                                    <span v-else class="la-ppx-marker__chip is-plain">{{ perplexityPayload.marker.label }}</span>
                                    <span v-if="perplexityPayload.marker.extra" class="la-ppx-marker__more" :title="`${perplexityPayload.marker.extra} weitere Quellen`">
                                        +{{ perplexityPayload.marker.extra }}
                                    </span>
                                </div>

                                <!-- Fanout-Queries: Suchanfragen, die das Modell aus dem Prompt abgeleitet hat -->
                                <div v-if="fanoutQueriesForActiveResponse.length" class="la-queries la-queries--fanout">
                                    <v-tooltip top max-width="320">
                                        <template v-slot:activator="{ on, attrs }">
                                            <span class="la-queries__label" v-bind="attrs" v-on="on">
                                                <v-icon x-small class="mr-1" color="primary">mdi-source-branch</v-icon>
                                                Fanout queries ({{ fanoutQueriesForActiveResponse.length }})
                                            </span>
                                        </template>
                                        <span>Web search queries the model derived from this prompt to research its answer</span>
                                    </v-tooltip>
                                    <div class="la-queries__chips">
                                        <span v-for="q in visibleFanoutQueries" :key="`fq-${q}`" class="la-query-chip">{{ q }}</span>
                                        <button
                                            v-if="fanoutQueriesForActiveResponse.length > fanoutQueriesCollapsedCount"
                                            type="button"
                                            class="la-query-chip la-query-chip--toggle"
                                            @click="showAllFanoutQueries = !showAllFanoutQueries"
                                        >
                                            {{ showAllFanoutQueries ? "Show less" : `+${fanoutQueriesForActiveResponse.length - fanoutQueriesCollapsedCount} more` }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="la-sidebar">
                            <div class="la-section-title-row">
                                <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="la-section-title">
                                            Sources
                                            <v-btn icon x-small class="la-help-icon" style="padding-bottom: 2px" v-bind="attrs" v-on="on" aria-label="Sources info">
                                                <v-icon small>mdi-help-circle-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Sources used to generate the text. They are not part of the response body.</span>
                                </v-tooltip>
                            </div>
                            <div v-if="normalizedSources.length" class="la-sources-list">
                                <a v-for="(s, idx) in normalizedSources" :key="`${s.key}-${idx}`" class="la-source-row" :href="s.href" target="_blank" rel="noopener noreferrer" :title="s.href">
                                    <div class="fav_wrapper" :class="{ fail: sourceFaviconFail[s.domain] }">
                                        <template v-if="!sourceFaviconFail[s.domain]">
                                            <img class="fav" :src="faviconUrl(s.domain, 64)" :alt="s.domain" @error="onSourceFavError(s.domain)" />
                                        </template>
                                        <template v-else>
                                            <div class="domain-placeholder">{{ (s.domain || "?").charAt(0).toUpperCase() }}</div>
                                        </template>
                                    </div>

                                    <div class="domain-meta">
                                        <div class="domain-text">{{ s.domain || "–" }}</div>
                                        <div v-if="s.pathLabel" class="domain-path">{{ s.pathLabel }}</div>
                                    </div>

                                    <v-icon x-small class="ml-auto" color="grey">mdi-open-in-new</v-icon>
                                </a>
                            </div>
                            <div v-else class="grey--text text--darken-1 mb-3">No Sources used</div>

                            <v-divider class="my-3" />

                            <div class="la-section-title-row">
                                <v-tooltip bottom max-width="320">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="la-section-title">
                                            Links
                                            <v-btn icon x-small class="la-help-icon" style="padding-bottom: 2px" v-bind="attrs" v-on="on" aria-label="Sources info">
                                                <v-icon small>mdi-help-circle-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Shows every link that appears inside the response text — unlike Sources, which list the reference material used to generate the answer.</span>
                                </v-tooltip>
                            </div>
                            <div v-if="inlineLinksList.length" class="la-sources-list la-links-list">
                                <a v-for="link in inlineLinksList" :key="link.id" class="la-source-row la-link-row" :href="link.href" target="_blank" rel="noopener noreferrer" :title="link.href">
                                    <div class="fav_wrapper" :class="{ fail: !link.favicon }">
                                        <template v-if="link.favicon">
                                            <img class="fav" :src="link.favicon" :alt="link.domain || link.href" />
                                        </template>
                                        <template v-else>
                                            <div class="domain-placeholder">{{ (link.domain || link.href || "?").charAt(0).toUpperCase() }}</div>
                                        </template>
                                    </div>
                                    <div class="domain-meta">
                                        <div class="domain-text">{{ link.label }}</div>
                                        <div class="domain-path">{{ link.domain || link.href }}</div>
                                    </div>
                                    <v-icon x-small class="ml-auto" color="grey">mdi-open-in-new</v-icon>
                                </a>
                            </div>
                            <div v-else class="grey--text text--darken-1 mb-3">No inline links detected</div>

                            <v-divider class="my-3" />

                            <div class="la-section-title-row">
                                <v-tooltip bottom max-width="360">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="la-section-title">
                                            Relevant Entities
                                            <v-btn icon x-small class="la-help-icon" style="padding-bottom: 3px" v-bind="attrs" v-on="on" aria-label="Entities info">
                                                <v-icon small>mdi-help-circle-outline</v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>
                                        Relevant Entities extracted from the response. Entities which are not relevant in the Prompt Set of
                                        {{ project && project.title ? project.title : "this project" }}
                                        are excluded.
                                    </span>
                                </v-tooltip>
                                <v-switch v-model="showAllEntities" inset dense hide-details class="ma-0 ml-2" :label="showAllEntities ? 'All Entities' : 'Relevant Entities'" />
                            </div>

                            <v-chip-group v-if="entityTypesForHighlight.length" v-model="activeEntityTypes" multiple column class="la-entity-chip-group my-2">
                                <v-chip v-for="type in entityTypesForHighlight" :key="type" :value="type" :color="entityTypeColor(type)" filter outlined>
                                    {{ type }}
                                </v-chip>
                            </v-chip-group>

                            <div v-if="entitiesForHighlight.length" class="la-entities-list">
                                <div v-for="entity in entitiesForHighlight" :key="(entity.canonicalName || entity.name || 'unknown') + '-' + entity.type" class="la-entity-row">
                                    <span class="entity-dot" :style="entityDotStyle(entity)"></span>
                                    <div class="entity-meta">
                                        <div class="entity-name">
                                            <span class="entity-name-text">{{ entity.canonicalName || entity.name || "Unknown entity" }}</span>
                                            <v-tooltip bottom v-if="entity.sentiment_score != null">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <span class="entity-sentiment-badge" :class="sentimentBadgeClass(entity.sentiment_score)" v-bind="attrs" v-on="on">
                                                        {{ formatSentiment100(entity.sentiment_score) }}
                                                    </span>
                                                </template>
                                                <span>{{ sentimentTooltip(entity.sentiment_score) }}</span>
                                            </v-tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="grey--text text--darken-1">No relevant Entities.</div>
                        </div>
                    </div>

                    <v-card-text v-else class="grey--text text--darken-1">No response for this platform.</v-card-text>
                </v-card>
            </v-row>
            <!-- <v-row dense>
                <v-col cols="12" md="8">
                    <v-card class="mb-4 pa-3">
                        <div class="card-title-row">
                            <span class="card-title">Entwicklung nach Plattform</span>
                            <v-spacer />
                            <v-select dense hide-details outlined style="max-width: 180px" :items="metricOptions" v-model="selectedMetric" label="Metrik" />
                        </div>

                        <v-skeleton-loader v-if="loading" type="image" class="mt-2" />

                        <div v-else>
                            <div v-if="chartSeries.length">
                                <div v-for="serie in chartSeries" :key="serie.platform" class="mb-3">
                                    <div class="d-flex align-center mb-1">
                                        <span class="platform-dot" />
                                        <span class="subtitle-2">{{ serie.platform }}</span>
                                    </div>
                                    <v-sparkline :value="serie.values" :smooth="8" :line-width="2" :padding="8" />
                                </div>
                            </div>
                            <div v-else class="grey--text text--darken-1 text-center py-4">Keine Daten für diesen Zeitraum / Filter.</div>
                        </div>
                    </v-card>
                    <v-card class="mb-4 pa-0">
                        <v-toolbar dense flat>
                            <v-toolbar-title class="subtitle-2">Letzte Responses</v-toolbar-title>
                            <v-spacer />
                            <v-tabs v-if="recentPlatforms.length" v-model="activeRecentPlatform" background-color="transparent" show-arrows hide-slider>
                                <v-tab :class="['la-platform-tab-btn', { 'la-platform-tab-btn--active': activeRecentPlatform === 0 }]"> Alle </v-tab>
                                <v-tab v-for="(p, idx) in recentPlatforms" :key="p" :class="['la-platform-tab-btn', { 'la-platform-tab-btn--active': activeRecentPlatform === idx + 1 }]">
                                    {{ p || "–" }}
                                </v-tab>
                            </v-tabs>
                        </v-toolbar>

                        <v-divider />

                        <v-card-text class="pa-0">
                            <v-list dense two-line>
                                <template v-if="filteredRecentResponses.length">
                                    <v-list-item v-for="resp in filteredRecentResponses" :key="resp.id" @click="openResponse(resp)" class="pointer">
                                        <v-list-item-content>
                                            <v-list-item-title> {{ resp.platform || "–" }} · {{ resp.model || "?" }} </v-list-item-title>
                                            <v-list-item-subtitle>
                                                {{ formatDateTime(resp.importDate) }}
                                                <span v-if="resp.tokenUsage != null"> · Tokens: {{ Math.round(resp.tokenUsage) }} </span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                        <v-list-item-icon>
                                            <v-icon small>mdi-chevron-right</v-icon>
                                        </v-list-item-icon>
                                    </v-list-item>
                                </template>

                                <template v-else>
                                    <v-list-item>
                                        <v-list-item-content>
                                            <v-list-item-title class="grey--text text--darken-1"> Keine Responses für diese Plattform. </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-list>
                        </v-card-text>
                    </v-card>

                </v-col>

                <v-col cols="12" md="4">
                    <v-card class="mb-4 pa-3">
                        <div class="card-title-row">
                            <span class="card-title">Top Sources</span>
                        </div>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th class="text-left">Domain</th>
                                    <th class="text-right">Responses</th>
                                    <th class="text-right">Mentions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="src in topSources" :key="src.domain">
                                    <td>{{ src.domain || "–" }}</td>
                                    <td class="text-right">
                                        {{ src.responses != null ? src.responses : "–" }}
                                    </td>
                                    <td class="text-right">
                                        {{ src.mentions != null ? src.mentions : "–" }}
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card>

                    <v-card class="mb-4 pa-3">
                        <div class="card-title-row">
                            <span class="card-title">Entities</span>
                        </div>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th class="text-left">Name</th>
                                    <th class="text-left">Typ</th>
                                    <th class="text-right">Mentions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="entity in entities" :key="entity.name + '-' + entity.type">
                                    <td>{{ entity.name }}</td>
                                    <td>{{ entity.type }}</td>
                                    <td class="text-right">{{ entity.mentions }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </v-row> -->
        </v-container>
    </div>
</template>

<script>
    import MarkdownIt from "markdown-it"
    import { AMAZON_RUFUS_LOGO } from "../../utils/platformMeta"
    import { getBrandColorFromName } from "../../utils/brandColor"
    export default {
        name: "PromptOverview",
        props: {
            // Overlay-Modus: eingebettet in AIProject als Drawer statt eigener Route
            embedded: { type: Boolean, default: false },
            // Query-förmige Parameter (task_id, prompt, platform, dates, brand, project_id),
            // gleiche Struktur wie die Route-Query der Standalone-Variante
            overlayParams: { type: Object, default: null }
        },
        data() {
            return {
                activeEntityTypes: [],
                sourceFaviconFail: {},
                selectedDate: null, // Einzelnes Datum als YYYY-MM-DD
                availableDates: [],
                datepickerMenu: false,
                loading: false,
                error: null,
                project: null, // aus fetchProject
                promptInfo: null,
                summary: {},

                timeseries: [], // timeseries.byPlatform aus API
                perPlatform: [],
                topSources: [],
                entities: [],
                recentResponses: [],

                entitiesCache: {},
                entitiesLoading: false,
                entitiesError: null,
                brands: [],
                showAllEntities: false,

                showFullPrompt: false,
                showAllFanoutQueries: false,
                fanoutQueriesCollapsedCount: 8,

                // Filter
                filters: {
                    dates: [], // [from, to] als YYYY-MM-DD (hier: [date, date])
                    platforms: []
                },
                dateMenu: false,

                metricOptions: [
                    { text: "Responses", value: "responses" },
                    { text: "Ø Tokens", value: "avgTokenUsage" }
                ],
                selectedMetric: "responses",

                // aktiver Plattform-Tab für "Letzte Responses"
                activeRecentPlatform: 1,
                activeLatestPlatform: 0,

                _suppressPlatformTabsSync: false,
                _initialPlatformApplied: false,
                _initialPlatformTabsApplied: false,
                _initialDateApplied: false,

                md: new MarkdownIt()
            }
        },
        computed: {
            normalizedSources() {
                const srcs = this.latestResponseForActivePlatform && Array.isArray(this.latestResponseForActivePlatform.sources) ? this.latestResponseForActivePlatform.sources : []
                return srcs.map((src, idx) => this.normalizeSource(src, idx)).filter((x) => x && x.domain)
            },
            projectBrandName() {
                return this.project && this.project.brand && this.project.brand.brand_name ? this.project.brand.brand_name : "–"
            },
            selectedDateText() {
                return this.selectedDate ? this.formatDate(this.selectedDate) : ""
            },
            processedDatesByPlatform() {
                if (!this.project) return []
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }
                // Aggregation für "all"
                if (true) {
                    const sets = []
                    Object.values(map).forEach((k) => {
                        const arr = this.project[k] || []
                        if (Array.isArray(arr)) sets.push(...arr)
                    })
                    // Unique + sort (ISO Strings)
                    return Array.from(new Set(sets)).sort()
                }
                const key = map[this.selectedPlatform.key || this.selectedPlatform] || "processed_dates_ai_tracking_openai"
                return this.project[key] || []
            },
            routeQuery() {
                // Im Overlay-Modus kommen die Parameter als Props statt aus der Route
                return this.overlayParams || (this.$route && this.$route.query) || {}
            },
            projectId() {
                // :id aus deiner Route /ai-suite/prompt-detail/:id (bzw. project_id aus overlayParams)
                return (this.overlayParams && this.overlayParams.project_id) || this.$route.params.id || null
            },
            taskId() {
                // task_id kommt als Query-Param (z.B. ?task_id=xyz)
                return this.routeQuery.task_id || null
            },
            promptFromRoute() {
                // optionaler Fallback, wenn du den Prompt-Text als query mitschickst
                return this.routeQuery.prompt || null
            },
            queryPlatform() {
                return this.routeQuery.platform || null
            },
            routeBrandFromQuery() {
                const raw = this.routeQuery.brand || null
                if (!raw) return null
                if (typeof raw === "object") return raw

                const parseJson = (value) => {
                    try {
                        const parsed = JSON.parse(value)
                        return parsed && typeof parsed === "object" ? parsed : null
                    } catch (e) {
                        return null
                    }
                }

                const direct = parseJson(String(raw))
                if (direct) return direct

                try {
                    const decoded = decodeURIComponent(String(raw))
                    return parseJson(decoded)
                } catch (e) {
                    return null
                }
            },
            promptTitle() {
                if (this.promptInfo && this.promptInfo.prompt) {
                    return this.promptInfo.prompt
                }
                if (this.promptFromRoute) return this.promptFromRoute
                return ""
            },
            promptText() {
                if (this.promptInfo && this.promptInfo.prompt) return this.promptInfo.prompt
                if (this.promptFromRoute) return this.promptFromRoute
                return ""
            },
            truncatedPrompt() {
                if (!this.promptText) return ""
                if (this.promptText.length <= 220) return this.promptText
                return this.promptText.slice(0, 220) + "..."
            },
            dateRangeLabel() {
                if (!this.filters.dates || this.filters.dates.length === 0) {
                    return "Letzte 30 Tage"
                }
                const [from, to] = this.filters.dates
                if (!to) return this.formatDate(from)
                return `${this.formatDate(from)} – ${this.formatDate(to)}`
            },
            platformOptions() {
                const set = new Set(this.timeseries.map((t) => t.platform).filter(Boolean))
                return Array.from(set).map((p) => ({ text: p, value: p }))
            },
            chartSeries() {
                const metric = this.selectedMetric
                const grouped = {}
                this.timeseries.forEach((row) => {
                    if (this.filters.platforms.length && !this.filters.platforms.includes(row.platform)) {
                        return
                    }
                    const key = row.platform || "Unknown"
                    if (!grouped[key]) grouped[key] = []
                    const value = metric === "responses" ? row.responses || 0 : row.avgTokenUsage || 0
                    grouped[key].push(value)
                })
                return Object.keys(grouped).map((platform) => ({
                    platform,
                    values: grouped[platform]
                }))
            },

            // Plattform-Liste aus den jüngsten Responses für Tabs
            recentPlatforms() {
                const set = new Set(this.recentResponses.map((r) => r.platform).filter(Boolean))
                return Array.from(set).sort()
            },

            // Responses nach gewähltem Tab gefiltert
            filteredRecentResponses() {
                if (!this.recentResponses.length) return []

                // Tab 0 = "Alle"
                if (this.activeRecentPlatform === 0 || this.activeRecentPlatform == null) {
                    return this.recentResponses
                }

                // Index 1..n entspricht recentPlatforms[0..n-1]
                const idx = this.activeRecentPlatform - 1
                const platform = this.recentPlatforms[idx]
                if (!platform) return []

                return this.recentResponses.filter((r) => (r.platform || null) === platform)
            },

            // jeweils letzte/aktuellste Response pro Plattform (für die zweite Card)
            latestResponseForActivePlatform() {
                if (!this.recentResponses.length || !this.recentPlatforms.length) return null

                // Nur Responses des gewählten Tages
                const sameDay = this.recentResponses.filter((r) => {
                    if (!this.selectedDate || !r.importDate) return false
                    return new Date(r.importDate).toISOString().slice(0, 10) === this.selectedDate
                })
                if (!sameDay.length) return null

                // aktiver Index in recentPlatforms (0..n-1)
                const idx = this.activeLatestPlatform || 0
                const platform = this.recentPlatforms[idx]
                if (!platform) return null

                // gleiche Plattform + bereits nach importDate DESC sortiert
                return sameDay.find((r) => (r.platform || null) === platform) || null
            },

            // === recentResponses: "aktueller Tag" und "vorheriger Tag" anhand importDate bestimmen ===
            recentResponsesSorted() {
                const rr = Array.isArray(this.recentResponses) ? [...this.recentResponses] : []
                rr.sort((a, b) => new Date(b.importDate || 0) - new Date(a.importDate || 0))
                return rr
            },
            currentRRDay() {
                const first = this.recentResponsesSorted[0]
                if (!first || !first.importDate) return null
                return new Date(first.importDate).toISOString().slice(0, 10)
            },
            previousRRDay() {
                const cur = this.currentRRDay
                if (!cur) return null
                const rr = this.recentResponsesSorted
                const next = rr.find((r) => r && r.importDate && new Date(r.importDate).toISOString().slice(0, 10) !== cur)
                return next && next.importDate ? new Date(next.importDate).toISOString().slice(0, 10) : null
            },

            // === Brand Visibility (current/previous) aus recentResponses (nach Plattform aggregiert) ===
            brandVisibilityCurrent() {
                return this.getBrandVisibilityFromRecentResponsesDay(this.currentRRDay)
            },
            brandVisibilityPrevious() {
                return this.getBrandVisibilityFromRecentResponsesDay(this.previousRRDay)
            },

            // Liste der Plattformen mit Flag, ob Brand dort am "currentRRDay" sichtbar ist
            brandVisibilityPlatformsCurrent() {
                const day = this.currentRRDay
                if (!day) return []

                const rr = Array.isArray(this.recentResponses) ? this.recentResponses : []

                const dayResponses = rr.filter((r) => {
                    if (!r || !r.importDate) return false
                    return new Date(r.importDate).toISOString().slice(0, 10) === day
                })
                if (!dayResponses.length) return []

                const platforms = Array.from(new Set(dayResponses.map((r) => r.platform).filter(Boolean)))

                return platforms.map((platform) => {
                    const responsesOfPlatform = dayResponses.filter((r) => r.platform === platform)
                    const visible = responsesOfPlatform.some((r) => this.isBrandVisibleInResponse(r))
                    return { platform, visible }
                })
            },

            // Text in der Card (Current)
            brandVisibilityCountText() {
                if (!this.brandVisibilityCurrent) return "—"
                return `${this.brandVisibilityCurrent.visible}/${this.brandVisibilityCurrent.total}`
            },

            // Status/Badge wie bisher (bezogen auf Current)
            brandVisibleAll() {
                if (!this.brandVisibilityCurrent) return null
                return this.brandVisibilityCurrent.total > 0 ? this.brandVisibilityCurrent.visible === this.brandVisibilityCurrent.total : null
            },
            brandVisibilityBadgeText() {
                if (this.brandVisibleAll === true) return "All visible"
                if (this.brandVisibleAll === false) {
                    const missing = this.brandVisibilityCurrent.total - this.brandVisibilityCurrent.visible
                    return `${missing} missing`
                }
                return "—"
            },

            // Optional: Change (wie Salience), falls du es anzeigen willst
            brandVisibilityDelta() {
                if (!this.brandVisibilityCurrent || !this.brandVisibilityPrevious) return null
                return this.brandVisibilityCurrent.visible - this.brandVisibilityPrevious.visible
            },
            brandVisibilityDeltaPercent() {
                if (!this.brandVisibilityCurrent || !this.brandVisibilityPrevious) return null
                if (Number(this.brandVisibilityPrevious.visible) === 0) return null
                return ((this.brandVisibilityCurrent.visible - this.brandVisibilityPrevious.visible) / this.brandVisibilityPrevious.visible) * 100
            },

            salienceCurrentDay() {
                // wenn der Nutzer im Datepicker ein Datum gewählt hat, nimm das
                if (this.selectedDate) return this.selectedDate
                // sonst Fallback: letzter Tag in recentResponses (wie bisher)
                return this.currentRRDay
            },
            // Vortag relativ zu salienceCurrentDay
            saliencePreviousDay() {
                const cur = this.salienceCurrentDay
                if (!cur) return null
                const d = new Date(cur)
                d.setDate(d.getDate() - 1)
                return d.toISOString().slice(0, 10)
            },

            salienceCurrent() {
                // Summe über alle Responses des ausgewählten Tages (oder letzten, falls nichts gewählt)
                return this.sumBrandSalienceForDay(this.salienceCurrentDay)
            },
            saliencePrevious() {
                // Summe über alle Responses des Vortags relativ zu Current
                return this.sumBrandSalienceForDay(this.saliencePreviousDay)
            },
            salienceDelta() {
                if (this.salienceCurrent == null || this.saliencePrevious == null) return null
                return this.salienceCurrent - this.saliencePrevious
            },
            salienceDeltaPercent() {
                if (this.salienceCurrent == null || this.saliencePrevious == null) return null
                if (Number(this.saliencePrevious) === 0) return null
                return ((this.salienceCurrent - this.saliencePrevious) / this.saliencePrevious) * 100
            },
            formatSalienceDeltaPercent() {
                if (this.salienceDeltaPercent == null) return "—"
                const v = Number(this.salienceDeltaPercent)
                if (!Number.isFinite(v)) return "—"
                return (v > 0 ? "+" : "") + v.toFixed(2) + "%"
            },

            salienceDeltaText() {
                if (this.salienceDelta == null) return "–"
                const sign = this.salienceDelta > 0 ? "+" : this.salienceDelta < 0 ? "" : "±"
                return `${sign}${Number(this.salienceDelta).toFixed(2)}`
            },

            brandDomain() {
                const raw = this.project && this.project.brand ? this.project.brand.brand_domain : ""
                if (!raw) return ""
                return String(raw)
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split("/")[0]
            },
            brandFaviconUrl() {
                return this.brandDomain ? "https://s2.googleusercontent.com/s2/favicons?domain=" + this.brandDomain + "&sz=32" : null
            },
            brandSentimentCurrent() {
                return this.computeBrandSentimentForDay(this.salienceCurrentDay)
            },
            brandSentimentPrevious() {
                return this.computeBrandSentimentForDay(this.saliencePreviousDay)
            },
            brandSentimentDelta() {
                if (this.brandSentimentCurrent == null || this.brandSentimentPrevious == null) return null
                return this.brandSentimentCurrent - this.brandSentimentPrevious
            },
            brandSentimentDisplayValue() {
                return this.formatSentimentValue(this.brandSentimentCurrent)
            },
            brandSentimentDeltaText() {
                return this.brandSentimentDelta != null ? this.formatSentimentDelta(this.brandSentimentDelta) : "—"
            },
            currentResponsePlatformKey() {
                const platform = this.latestResponseForActivePlatform && this.latestResponseForActivePlatform.platform ? this.latestResponseForActivePlatform.platform : ""
                return String(platform).trim().toLowerCase()
            },
            isPerplexityPlatform() {
                return this.currentResponsePlatformKey === "perplexity"
            },
            isAiModePlatform() {
                return this.currentResponsePlatformKey === "google_ai_mode"
            },
            // Perplexity haengt die Quellenangabe als KLARTEXT ans Textende: "… Sale-Seite. breuninger +4"
            // (Label der ersten Quelle + Anzahl der restlichen). Da kein <a>-Tag existiert, greift weder
            // forceChipLinks noch extractInlineLinks - der Marker blieb roher Text ohne Favicon.
            perplexityPayload() {
                const empty = { text: "", marker: null }
                if (!this.isPerplexityPlatform) return empty

                const raw = this.latestResponseForActivePlatform && this.latestResponseForActivePlatform.responseText ? this.latestResponseForActivePlatform.responseText : ""
                if (!raw) return empty

                // Bewusst nur ein einzelnes Token als Label: Perplexity nutzt den Domainnamen. Mehrwortige
                // Labels zu erlauben wuerde den vorangehenden Satz mit einsammeln.
                const m = raw.match(/\s([\p{L}\p{N}][\p{L}\p{N}.-]{0,40})\s+\+(\d+)\s*$/u)
                if (!m) return { text: raw, marker: null }

                // Der Marker steht immer hinter einem abgeschlossenen Satz. Ohne diese Pruefung wuerde
                // ein legitimes Textende wie "… liegt bei +5" faelschlich als Quellenangabe gelesen.
                const before = raw.slice(0, m.index).trimEnd()
                if (!/[.!?:)\]"»]$/.test(before)) return { text: raw, marker: null }

                return {
                    text: before,
                    marker: { label: m[1], extra: Number(m[2]) || 0 }
                }
            },
            perplexityMarkerSource() {
                const marker = this.perplexityPayload.marker
                if (!marker) return null

                const norm = (s) =>
                    String(s || "")
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, "")
                const label = norm(marker.label)
                if (!label) return null

                return (
                    this.normalizedSources.find((s) => {
                        const domain = norm(s.domain)
                        const sld = norm(String(s.domain || "").split(".")[0])
                        return sld === label || domain.startsWith(label)
                    }) || null
                )
            },
            aiModePayload() {
                const empty = { introHtml: "", results: [] }
                if (!this.isAiModePlatform) return empty

                const responseText = this.latestResponseForActivePlatform && this.latestResponseForActivePlatform.responseText ? this.latestResponseForActivePlatform.responseText : ""
                const results = this.parseAiModeResults(responseText)
                // Bei weniger als zwei Treffern ist es keine Ergebnisliste - dann bleibt der normale Markdown-Pfad.
                if (results.length < 2) return empty

                const introRaw = responseText.slice(0, responseText.indexOf("[](")).trim()
                return {
                    introHtml: introRaw ? this.md.renderInline(introRaw) : "",
                    results
                }
            },
            brandTokensSet() {
                return this.collectBrandTokensFromBrands(this.brands)
            },
            entitiesForActiveSource() {
                const resp = this.latestResponseForActivePlatform
                if (!resp) return []

                const merged = Array.isArray(resp.mergedEntities) ? resp.mergedEntities : []
                const all = Array.isArray(resp.allEntities) ? resp.allEntities : []

                if (this.showAllEntities) {
                    return all.length ? all : merged
                }
                return merged
            },
            entitiesForHighlight() {
                const list = this.entitiesForActiveSource
                if (!list.length) return []
                return list
            },
            entityTypesForHighlight() {
                if (!this.entitiesForHighlight.length) return []
                return [...new Set(this.entitiesForHighlight.map((e) => e.type).filter(Boolean))]
            },
            renderedResponsePayload() {
                const raw = this.latestResponseForActivePlatform && this.latestResponseForActivePlatform.responseText ? this.latestResponseForActivePlatform.responseText : ""
                // Bei Perplexity ist der Quellen-Marker bereits abgetrennt und wird separat als Chip gerendert.
                const responseText = this.isPerplexityPlatform ? this.perplexityPayload.text : raw
                return this.getRenderedResponsePayload(responseText, {
                    forceChipLinks: this.isPerplexityPlatform,
                    // Nicht auf die Plattform gaten: ein Link, dessen sichtbarer Text seine eigene Domain
                    // ist, ist nie Prosa, sondern immer eine Quellenangabe. Gemini & Co. liefern denselben
                    // Zitatstil wie ChatGPT, waren hier aber bisher ausgeschlossen.
                    chipDomainLinks: true
                })
            },
            shoppingItemsForActiveResponse() {
                const items = this.latestResponseForActivePlatform && this.latestResponseForActivePlatform.shopping_items
                if (!Array.isArray(items)) return []
                return [...items].sort((a, b) => (a.rank || 0) - (b.rank || 0))
            },
            shoppingQueriesForActiveResponse() {
                // Unique interne Katalog-Queries aus den Shopping-Items der aktiven Response
                const seen = new Set()
                const out = []
                this.shoppingItemsForActiveResponse.forEach((item) => {
                    const s = (item && item.shopping_query ? String(item.shopping_query) : "").trim()
                    if (!s) return
                    const key = s.toLowerCase()
                    if (seen.has(key)) return
                    seen.add(key)
                    out.push(s)
                })
                return out
            },
            fanoutQueriesForActiveResponse() {
                // ChatGPT: fan_out_queries, Perplexity/Grok: web_search_query
                const resp = this.latestResponseForActivePlatform
                if (!resp) return []
                const raw = [...(Array.isArray(resp.fan_out_queries) ? resp.fan_out_queries : []), ...(Array.isArray(resp.web_search_query) ? resp.web_search_query : [])]
                const seen = new Set()
                const out = []
                raw.forEach((q) => {
                    const s = (q == null ? "" : String(q)).trim()
                    if (!s) return
                    const key = s.toLowerCase()
                    if (seen.has(key)) return
                    seen.add(key)
                    out.push(s)
                })
                return out
            },
            visibleFanoutQueries() {
                if (this.showAllFanoutQueries) return this.fanoutQueriesForActiveResponse
                return this.fanoutQueriesForActiveResponse.slice(0, this.fanoutQueriesCollapsedCount)
            },
            inlineLinksList() {
                // Im AI-Mode-Listenpfad laeuft der Text nicht mehr durch extractInlineLinks, und selbst dort
                // waeren die Treffer unsichtbar: [](url) hat ein leeres Label, wird also immer zum Chip und
                // von extractInlineLinks bewusst herausgefiltert. Die Web-Ergebnisse SIND aber Links und
                // gehoeren in die Links-Spalte - daher hier direkt aus den geparsten Eintraegen speisen.
                const aiModeResults = this.aiModePayload.results
                if (aiModeResults.length) {
                    return aiModeResults.map((r, idx) => ({
                        id: `${r.domain || "link"}-${idx}-${r.url}`,
                        href: r.url,
                        domain: (r.domain || "").replace(/^www\./i, ""),
                        label: r.name || (r.domain || "").replace(/^www\./i, "") || r.url,
                        favicon: r.image || (r.domain ? this.faviconUrl(r.domain, 32) : "")
                    }))
                }
                return this.renderedResponsePayload.inlineLinks || []
            }
        },
        created() {
            // Default: letzte 30 Tage
            this.fetchProject(this.projectId)
        },
        watch: {
            recentPlatforms(newVal) {
                const len = Array.isArray(newVal) ? newVal.length : 0
                if (this.activeLatestPlatform >= len) this.activeLatestPlatform = 0
                if (this.activeRecentPlatform > len) this.activeRecentPlatform = 0
            },
            activeLatestPlatform(newVal) {
                if (this._suppressPlatformTabsSync) return
                const platform = this.recentPlatforms[newVal] || null
                this.selectPlatform(platform)
            },
            activeRecentPlatform(newVal) {
                if (this._suppressPlatformTabsSync) return
                if (newVal === 0) {
                    this.selectPlatform(null)
                    return
                }
                const idx = newVal - 1
                const platform = this.recentPlatforms[idx] || null
                this.selectPlatform(platform)
            },
            latestResponseForActivePlatform: {
                immediate: true,
                handler(resp) {
                    this.showAllFanoutQueries = false
                    this.loadEntitiesForVisibleResponse(resp)
                }
            }
        },
        methods: {
            goBack() {
                if (this.embedded) {
                    this.$emit("close")
                    return
                }
                this.$router.back()
            },
            async fetchEntities(response_id) {
                if (!response_id) return []

                try {
                    const proxyList = JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS || "[]")
                    const decryptedCompanyId = this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    const useProxy = proxyList.indexOf(decryptedCompanyId) === -1
                    const base = useProxy ? process.env.VUE_APP_MONITORING_API_URL : process.env.VUE_APP_APIURL + "/monitoring-api"
                    const url = `${base}/ai-suite/response/entities`

                    const post_data = {
                        company_id: localStorage.company_id,
                        response_id
                    }

                    const response_data = await this.$helpers.axiosPost(url, post_data)
                    const entities = response_data && response_data.data && response_data.data.entities && (response_data.data.entities.entities || response_data.data.entities)

                    return Array.isArray(entities) ? entities : []
                } catch (error) {
                    console.log("fetchEntities error:", error)
                    throw error
                }
            },
            async loadEntitiesForVisibleResponse(resp) {
                if (!resp) return

                const responseId = resp.id || resp._id || resp.response_id
                if (!responseId) return

                const alreadyLoaded = this.entitiesCache[responseId]
                // if (alreadyLoaded && Array.isArray(alreadyLoaded)) {
                //     this.$set(resp, "mergedEntities", alreadyLoaded)
                //     return
                // }

                // if (resp.mergedEntities && Array.isArray(resp.mergedEntities) && resp.mergedEntities.length) {
                //     this.$set(this.entitiesCache, responseId, resp.mergedEntities)
                //     return
                // }

                this.entitiesLoading = true
                this.entitiesError = null

                try {
                    const entities = await this.fetchEntities(responseId)
                    const list = Array.isArray(entities) ? entities : []
                    this.$set(this.entitiesCache, responseId, list)
                    this.$set(resp, "allEntities", list)
                } catch (error) {
                    this.entitiesError = "Fehler beim Laden der Entities."
                } finally {
                    this.entitiesLoading = false
                }
            },
            clamp(n, min, max) {
                const x = Number(n)
                if (!Number.isFinite(x)) return min
                return Math.min(max, Math.max(min, x))
            },

            formatSentiment100(val) {
                const num = Number(val)
                if (!Number.isFinite(num)) return "–"
                const scaled = this.clamp(num, -1, 1) * 100
                return `${scaled > 0 ? "+" : ""}${scaled.toFixed(0)}`
            },
            sentimentTooltip(score) {
                const num = Number(score)
                if (!Number.isFinite(num)) return "Sentiment score unavailable"
                const clamped = this.clamp(num, -1, 1)
                const pct = clamped * 100
                const sign = pct > 0 ? "+" : ""
                return `Sentiment score: ${sign}${pct.toFixed(0)} on a scale from -100 (negative) to +100 (positive)`
            },
            sentimentBadgeClass(score) {
                const v = Number(score)
                if (!Number.isFinite(v)) return "is-na"
                if (v >= 0.15) return "is-pos"
                if (v <= -0.15) return "is-neg"
                return "is-neu"
            },

            parseQueryArray(value) {
                if (!value) return []
                if (Array.isArray(value)) return value
                try {
                    const parsed = JSON.parse(value)
                    return Array.isArray(parsed) ? parsed : []
                } catch (e) {
                    return String(value)
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean)
                }
            },
            formatShoppingPrice(item) {
                if (item.price == null) return ""
                const cur = item.currency === "EUR" ? "€" : item.currency ? item.currency + " " : ""
                return `${cur}${Number(item.price).toFixed(2)}`
            },
            normalizeDateKey(value) {
                if (!value) return null
                const str = String(value)
                if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
                const dt = new Date(str)
                if (Number.isNaN(dt.getTime())) return null
                return dt.toISOString().slice(0, 10)
            },
            getQueryDates() {
                const query = this.routeQuery || {}
                const raw = query.dates || query.date || null
                const parsed = this.parseQueryArray(raw)
                const normalized = parsed.map((d) => this.normalizeDateKey(d)).filter(Boolean)

                const from = this.normalizeDateKey(query.from)
                const to = this.normalizeDateKey(query.to)

                if (from || to) {
                    const first = from || to
                    const second = to || from || first
                    return [first, second].filter(Boolean)
                }

                if (!normalized.length) return []
                if (normalized.length === 1) return [normalized[0], normalized[0]]
                return normalized.slice(0, 2)
            },
            selectPlatform(platform) {
                const current = Array.isArray(this.filters.platforms) && this.filters.platforms.length ? this.filters.platforms[0] : null
                if ((current || null) === (platform || null)) return

                this._suppressPlatformTabsSync = true
                this.filters.platforms = platform ? [platform] : []

                if (platform) {
                    const idx = this.recentPlatforms.indexOf(platform)
                    if (idx >= 0) {
                        if (this.activeLatestPlatform !== idx) this.activeLatestPlatform = idx
                        if (this.activeRecentPlatform !== idx + 1) this.activeRecentPlatform = idx + 1
                    }
                } else {
                    if (this.activeRecentPlatform !== 0) this.activeRecentPlatform = 0
                }

                this.fetchOverview()

                this.$nextTick(() => {
                    this._suppressPlatformTabsSync = false
                })
            },
            async fetchProject(project_id) {
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/project/one"
                    let post_data = {
                        company_id: localStorage.company_id,
                        project_id: project_id
                    }
                    let projectData = await this.$helpers.axiosPost(url, post_data)
                    this.project = projectData.data
                    this.availableDates = [...new Set(this.processedDatesByPlatform.map((d) => new Date(d).toISOString().substring(0, 10)))]
                    const queryDates = this.getQueryDates()
                    if (queryDates.length && !this._initialDateApplied) {
                        const [from, to] = queryDates
                        this.filters.dates = [from, to || from].filter(Boolean)
                        const preferred = to || from
                        this.selectedDate = this.availableDates.includes(preferred) ? preferred : this.availableDates[this.availableDates.length - 1] || preferred || null
                        this._initialDateApplied = true
                    } else if (!this.selectedDate) {
                        this.selectedDate = this.availableDates[this.availableDates.length - 1] || null
                    }
                    if (this.queryPlatform && !this._initialPlatformApplied) {
                        this.filters.platforms = [this.queryPlatform]
                        this._initialPlatformApplied = true
                    }
                    this.fetchOverview()
                } catch (error) {
                    console.log(error)
                }
            },
            async fetchOverview() {
                this.loading = true
                this.error = null
                let requestPayload = null

                try {
                    if (!this.projectId) {
                        this.error = "Project ID fehlt in der Route."
                        this.loading = false
                        return
                    }

                    if (!this.taskId && !this.promptFromRoute) {
                        this.error = "Es wurde weder task_id noch prompt in der Route übergeben."
                        this.loading = false
                        return
                    }

                    const companyId = localStorage.company_id
                    if (!companyId) {
                        this.error = "company_id nicht im LocalStorage gefunden."
                        this.loading = false
                        return
                    }

                    // Base-URL wie in AIProject / PromptsTable
                    const decryptedCompanyId = this.CryptoJS.AES.decrypt(companyId, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)

                    const useProxy = JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS || "[]").indexOf(decryptedCompanyId) === -1

                    const base = useProxy ? process.env.VUE_APP_MONITORING_API_URL : process.env.VUE_APP_APIURL + "/monitoring-api"

                    const url = `${base}/ai-suite/response/overview`

                    const [from, to] = this.filters.dates || []
                    const body = {
                        company_id: companyId,
                        project_id: this.projectId,
                        task_id: this.taskId || undefined,
                        prompt: this.promptFromRoute || undefined,
                        from: from || undefined,
                        to: to || from || undefined,
                        platforms: this.filters.platforms.length ? this.filters.platforms : undefined
                    }
                    requestPayload = body

                    const res = await this.$helpers.axiosPost(url, body)
                    let data = res && res.data ? res.data : {}
                    console.log(data.summary)
                    // Fallback: falls dein Router aktuell { entities: results } zurückgibt
                    // if (data && data.entities) {
                    //     data = data.entities
                    // }

                    this.promptInfo = data.prompt || null
                    this.summary = data.summary
                    console.log(this.summary)
                    this.timeseries = (data.timeseries && data.timeseries.byPlatform) || []
                    this.perPlatform = data.perPlatform || []
                    this.topSources = data.topSources || []
                    this.entities = data.entities || []
                    this.recentResponses = data.recentResponses || []
                    this.brands = this.routeBrandFromQuery ? [this.routeBrandFromQuery] : []
                    if (this.queryPlatform && !this._initialPlatformTabsApplied) {
                        const idx = this.recentPlatforms.indexOf(this.queryPlatform)
                        if (idx >= 0) {
                            this.activeLatestPlatform = idx
                            this.activeRecentPlatform = idx + 1
                        }
                        this._initialPlatformTabsApplied = true
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("fetchOverview error:", e)
                    await this.notifyAdminError(e, { source: "PromptOverview.fetchOverview", requestPayload })
                    this.error = "Fehler beim Laden des Prompt-Overviews."
                } finally {
                    this.loading = false
                }
            },
            async notifyAdminError(e, contextData, responseData) {
                let errorPayload
                try {
                    // Send detailed context first; fallback below keeps admin notifications resilient.
                    errorPayload = {
                        type: "Error",
                        data: {
                            message: e?.message || String(e),
                            stack: e?.stack || null,
                            response: e?.response
                                ? {
                                      status: e.response.status,
                                      data: typeof e.response.data === "object" ? JSON.stringify(e.response.data) : String(e.response.data)
                                  }
                                : undefined,
                            context: {
                                ...(contextData || {}),
                                user_id: localStorage.id,
                                company_id: localStorage.company_id
                            },
                            responseData: responseData ? (typeof responseData === "object" ? JSON.stringify(responseData) : String(responseData)) : undefined
                        },
                        user: localStorage.username,
                        company_id: localStorage.company_id
                    }

                    await this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/service/notify/admin", errorPayload)
                } catch (notifyErr) {
                    try {
                        await this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/service/notify/admin", {
                            type: "Error",
                            data: {
                                message: "Error notification failed",
                                originalError: e && e.message ? e.message : String(e),
                                notifyError: notifyErr && notifyErr.message ? notifyErr.message : String(notifyErr)
                            },
                            user: localStorage.username || "-",
                            company_id: localStorage.company_id || "-"
                        })
                    } catch {
                        // No further fallback available here.
                        console.log("Admin notification failed completely", notifyErr)
                    }
                }
            },
            faviconUrl(domain, sz = 32) {
                if (!domain) return ""
                return `https://s2.googleusercontent.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${sz}`
            },
            onSourceFavError(domain) {
                if (!domain) return
                this.$set(this.sourceFaviconFail, domain, true)
            },
            normalizeSource(src, idx = 0) {
                const raw = src && typeof src === "object" ? src.url || src.href || src.link || src.domain : src
                if (!raw) return { href: "#", domain: "", label: "–", key: `src-${idx}`, path: "", pathLabel: "" }

                const s = String(raw).trim()
                const href = /^https?:\/\//i.test(s) ? s : `https://${s}`

                let domain = ""
                let path = ""
                try {
                    const u = new URL(href)
                    domain = u.hostname
                    path = (u.pathname || "/") + (u.search || "")
                } catch {
                    domain = s.replace(/^https?:\/\//i, "").split("/")[0]
                    path = s.includes("/") ? s.slice(s.indexOf("/")) : ""
                }
                domain = (domain || "").replace(/^www\./i, "")

                const pathLabel = path

                return {
                    href,
                    domain,
                    label: domain || s,
                    key: domain || href || `src-${idx}`,
                    path,
                    pathLabel
                }
            },
            normalizeText(s) {
                return String(s || "")
                    .trim()
                    .toLowerCase()
                    .replace(/[^\p{L}\p{N}\s.-]/gu, "")
                    .replace(/\s+/g, " ")
            },
            getImportDatesByPlatformForDay(day) {
                if (!this.project || !day) return []
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }

                const byPlatform = []
                Object.entries(map).forEach(([platform, field]) => {
                    const arr = Array.isArray(this.project[field]) ? this.project[field] : []
                    if (!arr.length) return

                    const dayDates = arr
                        .map((v) => new Date(v))
                        .filter((d) => !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === day)
                        .sort((a, b) => a.getTime() - b.getTime())

                    if (!dayDates.length) return

                    const first = dayDates[0].toISOString()
                    const last = dayDates[dayDates.length - 1].toISOString()
                    byPlatform.push({ platform, importDates: first === last ? [first] : [first, last] })
                })

                return byPlatform
            },
            normalizeBrandToken(value) {
                return this.normalizeText(value)
            },
            extractMentionTokenValue(mention) {
                if (mention == null) return ""
                if (typeof mention === "string" || typeof mention === "number") return String(mention)
                if (typeof mention !== "object") return ""

                return (mention.text && (mention.text.content || mention.text.snippet)) || mention.canonicalName || mention.name || mention.entity || mention.brand || mention.value || ""
            },
            getTokenVariants(value) {
                const base = this.normalizeBrandToken(value)
                if (!base) return []

                const variants = new Set([base])
                const withoutDotsDashes = base.replace(/[.-]/g, " ").replace(/\s+/g, " ").trim()
                if (withoutDotsDashes) variants.add(withoutDotsDashes)

                const compact = base.replace(/[.\-\s]/g, "").trim()
                if (compact) variants.add(compact)

                return Array.from(variants)
            },
            collectBrandTokensFromBrands(brands = []) {
                const out = new Set()
                if (!Array.isArray(brands)) return out

                brands.forEach((brand) => {
                    if (!brand) return

                    const canonical = this.normalizeBrandToken(brand.canonicalName || brand.brand_name || brand.brand)
                    if (canonical) out.add(canonical)

                    if (Array.isArray(brand.mentions)) {
                        brand.mentions.forEach((mention) => {
                            this.getTokenVariants(this.extractMentionTokenValue(mention)).forEach((token) => out.add(token))
                        })
                    }
                })

                return out
            },
            getEntityTokens(entity) {
                const out = new Set()
                if (!entity || typeof entity !== "object") return []
                ;[entity.canonicalName, entity.name, entity.brand].forEach((v) => {
                    this.getTokenVariants(v).forEach((token) => out.add(token))
                })

                if (Array.isArray(entity.mentions)) {
                    entity.mentions.forEach((m) => {
                        this.getTokenVariants(this.extractMentionTokenValue(m)).forEach((token) => out.add(token))
                    })
                }

                return Array.from(out)
            },
            isEntityInBrands(entity) {
                const brandTokens = this.brandTokensSet
                if (!brandTokens.size) return false

                const entityTokens = this.getEntityTokens(entity)
                if (!entityTokens.length) return false

                const comparableBrandTokens = new Set()
                brandTokens.forEach((bt) => {
                    this.getTokenVariants(bt).forEach((token) => comparableBrandTokens.add(token))
                })

                return entityTokens.some((et) => comparableBrandTokens.has(et))
            },
            async fetchBrandsAllPlatformsForEntry() {
                try {
                    if (!this.projectId || !this.selectedDate) {
                        this.brands = []
                        return
                    }

                    const importDatesByPlatform = this.getImportDatesByPlatformForDay(this.selectedDate)
                    if (!importDatesByPlatform.length) {
                        this.brands = []
                        return
                    }

                    const proxyList = JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS || "[]")
                    const decryptedCompanyId = this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    const useProxy = proxyList.indexOf(decryptedCompanyId) === -1
                    const base = useProxy ? process.env.VUE_APP_MONITORING_API_URL : process.env.VUE_APP_APIURL + "/monitoring-api"
                    const url = `${base}/ai-suite/brandvisibility/by/date`

                    const body = {
                        company_id: localStorage.company_id,
                        project_id: this.projectId,
                        platform: null,
                        importDatesByPlatform
                    }

                    const res = await this.$helpers.axiosPost(url, body)
                    const brandsByDate = res && res.data && Array.isArray(res.data.brandsByDate) ? res.data.brandsByDate : []
                    this.brands = brandsByDate.flat().filter(Boolean)
                } catch (e) {
                    this.brands = []
                }
            },

            // true, wenn Brand im Response "sichtbar" ist (über mergedEntities + mentions)
            isBrandVisibleInResponse(resp) {
                const list = Array.isArray(resp.mergedEntities) ? resp.mergedEntities : []
                if (!list.length) return false

                // Primär: Varianten aus ManageBrandDialog / this.brands (inkl. mentions)
                if (this.brandTokensSet && this.brandTokensSet.size) {
                    return list.some((entity) => this.isEntityInBrands(entity))
                }

                // Fallback: project.brand, falls (noch) keine brands-Daten geladen sind
                const brand = this.project && this.project.brand ? this.project.brand.brand_name : null
                if (!brand) return false

                const fallbackTokens = new Set(this.getTokenVariants(brand))
                if (!fallbackTokens.size) return false

                return list.some((entity) => {
                    const entityTokens = this.getEntityTokens(entity)
                    return entityTokens.some((token) => fallbackTokens.has(token))
                })
            },

            // Brand Visibility für einen Tag (YYYY-MM-DD) aus recentResponses aggregieren
            getBrandVisibilityFromRecentResponsesDay(day) {
                if (!day) return null
                const rr = Array.isArray(this.recentResponses) ? this.recentResponses : []

                const dayResponses = rr.filter((r) => {
                    if (!r || !r.importDate) return false
                    return new Date(r.importDate).toISOString().slice(0, 10) === day
                })

                if (!dayResponses.length) return { total: 0, visible: 0 }

                const platforms = Array.from(new Set(dayResponses.map((r) => r.platform).filter(Boolean)))
                const total = platforms.length

                const visiblePlatforms = new Set()
                dayResponses.forEach((r) => {
                    if (!r || !r.platform) return
                    if (this.isBrandVisibleInResponse(r)) visiblePlatforms.add(r.platform)
                })

                return { total, visible: visiblePlatforms.size }
            },

            /**
             * Summiert die Brand-Salience über alle Responses eines Tages (YYYY-MM-DD).
             * - Wenn es keine Responses an diesem Tag gibt: null
             * - Wenn es Responses gibt, aber keine Brand gefunden wird: 0
             */
            sumBrandSalienceForDay(day) {
                if (!day) return null
                const rr = Array.isArray(this.recentResponses) ? this.recentResponses : []

                const sameDay = rr.filter((r) => {
                    if (!r || !r.importDate) return false
                    return new Date(r.importDate).toISOString().slice(0, 10) === day
                })

                if (!sameDay.length) return null

                let total = 0
                let hasAny = false

                sameDay.forEach((r) => {
                    const s = this.getBrandSalienceFromRecentResponse(r)
                    if (s != null) {
                        total += Number(s) || 0
                        hasAny = true
                    }
                })

                return hasAny ? total : 0
            },

            // Salience bleibt: Brand finden und mentions/salience ausgeben (erweitert für mentions-Array)
            getBrandSalienceFromRecentResponse(resp) {
                const brand = this.project && this.project.brand ? this.project.brand.brand_name : null
                if (!brand || !resp || !resp.mergedEntities) return null

                const brandNorm = this.normalizeText(brand)
                const list = Array.isArray(resp.mergedEntities) ? resp.mergedEntities : []

                const matchesBrandName = (name) => {
                    const n = this.normalizeText(name)
                    if (!n) return false
                    return n === brandNorm || n.includes(brandNorm) || brandNorm.includes(n)
                }

                // 1) Entity über name matchen
                const entity = list.find((e) => matchesBrandName(e && e.name))
                if (entity) {
                    // a) Bevorzugt: entity.salience (so wie in deinem Beispiel)
                    if (entity.salience != null) {
                        const n = Number(entity.salience)
                        return Number.isFinite(n) ? n : null
                    }

                    // b) Falls Backend irgendwann mal eine Zahl in mentions direkt legt
                    if (typeof entity.mentions === "number") {
                        const n = Number(entity.mentions)
                        return Number.isFinite(n) ? n : null
                    }

                    // c) mentions als Array: ggf. pro-Mention-Salience
                    if (Array.isArray(entity.mentions)) {
                        const getMentionName = (x) => x && (x.name || x.brand || x.entity || (x.text && x.text.content))
                        const hit = entity.mentions.find((x) => matchesBrandName(getMentionName(x)))
                        if (hit) {
                            const v = hit.salience != null ? hit.salience : hit.mentions != null ? hit.mentions : null
                            const n = Number(v)
                            if (Number.isFinite(n)) return n
                        }
                    }
                }

                // 2) Fallback: in allen entities deren mentions[] nach Brand suchen
                for (const e of list) {
                    const m = e && e.mentions
                    if (!Array.isArray(m)) continue
                    const getMentionName = (x) => x && (x.name || x.brand || x.entity || (x.text && x.text.content))
                    const hit = m.find((x) => matchesBrandName(getMentionName(x)))
                    if (!hit) continue
                    const v = hit.salience != null ? hit.salience : hit.mentions != null ? hit.mentions : null
                    const n = Number(v)
                    if (Number.isFinite(n)) return n
                }

                return null
            },
            computeBrandSentimentForDay(day) {
                if (!day) return null
                const rr = Array.isArray(this.recentResponses) ? this.recentResponses : []

                const sameDay = rr.filter((r) => {
                    if (!r || !r.importDate) return false
                    return new Date(r.importDate).toISOString().slice(0, 10) === day
                })

                if (!sameDay.length) return null

                const values = sameDay.map((resp) => this.getBrandSentimentFromResponse(resp)).filter((val) => val != null && Number.isFinite(val))

                if (!values.length) return null

                const total = values.reduce((sum, val) => sum + val, 0)
                return total / values.length
            },
            getBrandSentimentFromResponse(resp) {
                const brand = this.project && this.project.brand ? this.project.brand.brand_name : null
                if (!brand || !resp || !Array.isArray(resp.mergedEntities)) return null

                const brandNorm = this.normalizeText(brand)
                if (!brandNorm) return null

                const matchesBrand = (raw) => {
                    const norm = this.normalizeText(raw)
                    if (!norm) return false
                    return norm === brandNorm || norm.includes(brandNorm) || brandNorm.includes(norm)
                }

                const findMentionMatch = (entity) => {
                    if (!entity || !Array.isArray(entity.mentions)) return null
                    return entity.mentions.find((mention) => matchesBrand(mention && (mention.name || mention.brand || mention.entity || (mention.text && mention.text.content))))
                }

                const list = resp.mergedEntities
                let entity = list.find((e) => matchesBrand(e && (e.canonicalName || e.name)))
                if (!entity) {
                    entity = list.find((e) => !!findMentionMatch(e))
                }
                if (!entity) return null

                const mentionMatch = findMentionMatch(entity)
                const rawSentiment =
                    entity.sentiment_score ??
                    entity.sentimentScore ??
                    entity.sentiment ??
                    (mentionMatch ? mentionMatch.sentiment_score ?? mentionMatch.sentimentScore ?? mentionMatch.sentiment ?? mentionMatch.score : null)

                if (rawSentiment == null) return null
                const num = Number(rawSentiment)
                if (!Number.isFinite(num)) return null
                return this.clampSentimentValue(num)
            },
            formatSentimentValue(value) {
                const clamped = this.clampSentimentValue(value)
                if (clamped == null) return "—"
                const scaled = clamped * 100
                if (scaled === 0) return "0"
                return `${scaled > 0 ? "+" : ""}${scaled.toFixed(0)}`
            },
            formatSentimentDelta(delta) {
                const num = Number(delta)
                if (!Number.isFinite(num)) return "—"
                const scaled = num * 100
                if (scaled === 0) return "±0"
                return `${scaled > 0 ? "+" : ""}${scaled.toFixed(0)}`
            },
            sentimentMarkerLeft(value) {
                const clamped = this.clampSentimentValue(value)
                if (clamped == null) return "50%"
                const pct = ((clamped + 1) / 2) * 100
                return `calc(${pct.toFixed(2)}% - 6px)`
            },
            clampSentimentValue(value) {
                if (value == null && value !== 0) return null
                const num = Number(value)
                if (!Number.isFinite(num)) return null
                return Math.max(-1, Math.min(1, num))
            },
            async onFilterChange() {
                this.fetchOverview()
            },
            async onDateChange(val) {
                // v-date-picker gibt ein String-ISO-Datum (YYYY-MM-DD) zurück
                this.selectedDate = val
                if (val) {
                    // ein Tag: from = to = selectedDate
                    this.filters.dates = [val, val]
                } else {
                    this.filters.dates = []
                }
                this.fetchOverview()
                this.datepickerMenu = false
            },
            openResponse(resp) {
                // später: eigener Detail-View oder Dialog
                // aktuell nur Log als Platzhalter
                // eslint-disable-next-line no-console
                console.log("Open response detail", resp)
            },
            toPickerDate(d) {
                const yyyy = d.getFullYear()
                const mm = String(d.getMonth() + 1).padStart(2, "0")
                const dd = String(d.getDate()).padStart(2, "0")
                return `${yyyy}-${mm}-${dd}`
            },
            formatDate(dateStr) {
                if (!dateStr) return ""
                const d = new Date(dateStr)
                return d.toLocaleDateString()
            },
            formatSalience(val) {
                if (val == null || val === "") return "–"
                const num = Number(val)
                return Number.isFinite(num) ? (num * 100).toFixed(2) : "–"
            },
            formatDateTime(dateStr) {
                if (!dateStr) return ""
                const d = new Date(dateStr)
                return d.toLocaleString()
            },
            allowedDates(val) {
                return this.availableDates.includes(val)
            },
            getRenderedResponsePayload(text = "", options = {}) {
                const { forceChipLinks = false, chipDomainLinks = false } = options || {}
                const safeText = typeof text === "string" ? text : ""
                if (!safeText.trim()) {
                    return { html: "–", inlineLinks: [] }
                }

                let html = this.md.render(safeText)
                html = this.highlightEntitiesInHtml(html)
                html = this.replaceSourcesWithChips(html, { forceChipLinks, chipDomainLinks })
                const inlineLinks = this.extractInlineLinks(html)
                return { html, inlineLinks }
            },
            // google_ai_mode liefert keine Prosa, sondern eine Liste von Web-Ausschnitten in EINEM Fliesstext:
            //   [](ziel-url) ![](bild-url)\nName·https://domain Titel und Snippet
            // Der naechste Marker klebt ohne Trennzeichen am vorigen Snippet, deshalb rendert markdown-it
            // alle Treffer zu einem einzigen Absatz. Hier wird der Text wieder in Eintraege zerlegt.
            parseAiModeResults(text = "") {
                const src = String(text || "")
                if (!src.trim()) return []

                const marker = /\[\]\((https?:\/\/[^)\s]+)\)\s*(?:!\[\]\((https?:\/\/[^)\s]+)\))?[ \t]*\n?/g
                const hits = []
                let m
                while ((m = marker.exec(src)) !== null) {
                    hits.push({ start: m.index, end: marker.lastIndex, url: m[1], image: m[2] || "" })
                }
                if (!hits.length) return []

                return hits.map((hit, i) => {
                    const body = src.slice(hit.end, i + 1 < hits.length ? hits[i + 1].start : src.length).trim()

                    // "Name·https://domain Rest" - der Trenner ist U+00B7
                    const parts = body.match(/^(.*?)·(https?:\/\/\S+)\s*([\s\S]*)$/)
                    const name = parts ? parts[1].trim() : ""
                    const domainRaw = parts ? parts[2] : hit.url
                    const snippet = parts ? parts[3].trim() : body

                    let domain = ""
                    try {
                        domain = new URL(domainRaw).hostname
                    } catch (e) {
                        domain = String(domainRaw).replace(/^https?:\/\//i, "").split("/")[0]
                    }

                    return {
                        url: hit.url,
                        image: hit.image,
                        name,
                        domain,
                        snippet,
                        html: snippet ? this.highlightEntitiesInHtml(this.md.renderInline(snippet)) : ""
                    }
                })
            },
            onAiModeImgError(event) {
                if (event && event.target) event.target.style.visibility = "hidden"
            },
            highlightEntitiesInHtml(html) {
                if (!html || !Array.isArray(this.entitiesForHighlight)) return html

                const active =
                    Array.isArray(this.activeEntityTypes) && this.activeEntityTypes.length
                        ? this.entitiesForHighlight.filter((e) => this.activeEntityTypes.includes(e.type))
                        : this.entitiesForHighlight

                active
                    .filter((e) => e && (e.canonicalName || e.name || (Array.isArray(e.mentions) && e.mentions.length)))
                    .forEach((entity) => {
                        const typeClass = (entity.type || "OTHER").toLowerCase().replace(/ /g, "_")
                        const names = new Set()

                        if (entity.canonicalName) names.add(String(entity.canonicalName))
                        if (entity.name) names.add(String(entity.name))

                        if (Array.isArray(entity.mentions)) {
                            entity.mentions.forEach((m) => {
                                const raw = (m && m.text && (m.text.content || m.text.snippet)) || (m && (m.canonicalName || m.name || m.entity || m.brand)) || m
                                if (raw) names.add(String(raw))
                            })
                        }

                        Array.from(names)
                            .filter(Boolean)
                            .sort((a, b) => b.length - a.length)
                            .forEach((name) => {
                                const safeName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                                const regex = new RegExp(`(>[^<]*?)(?<![\\p{L}\\p{N}])(${safeName})(?![\\p{L}\\p{N}])`, "giu")
                                html = html.replace(regex, (match, prefix, found) => `${prefix}<span class="la-entity-highlight entity-${typeClass}">${found}</span>`)
                            })
                    })

                return html
            },
            entityTypeColor(type) {
                const colors = {
                    ORGANIZATION: "blue",
                    PERSON: "success",
                    LOCATION: "info",
                    PRODUCT: "warning",
                    EVENT: "error",
                    OTHER: "grey",
                    CONSUMER_GOOD: "#fbc02d"
                }
                return colors[type] || "grey"
            },
            entityDotStyle(entity) {
                const label = (entity?.canonicalName || entity?.name || "unknown").toString().toLowerCase()
                return {
                    backgroundColor: getBrandColorFromName(label)
                }
            },

            replaceSourcesWithChips(html, options = {}) {
                if (!html) return html
                const { forceChipLinks = false, chipDomainLinks = false } = options || {}

                const isDomainOrUrl = (text) => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([\/?#]\S*)?$/i.test((text || "").trim())

                // ChatGPT liefert Zitat-Labels mal als Host ("www.adac.de"), mal als Publisher-Name ("ADAC").
                // Nur der Name-Fall braucht Zusatzlogik. Beschreibende Prosa-Links (z.B. Titel eines PDF-Leitfadens)
                // sollen bewusst Textlinks bleiben und nicht zu einem nackten Favicon zusammenfallen.
                const normLabel = (s = "") => s.toLowerCase().replace(/[^a-z0-9]/g, "")
                const isSourceNameLabel = (visibleText = "", href = "") => {
                    const label = (visibleText || "").trim()
                    if (!label || label.split(/\s+/).length > 3) return false

                    const url = (href || "").replace(/&amp;/gi, "&").trim()
                    let host = ""
                    try {
                        host = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname
                    } catch {
                        return false
                    }

                    // Label ist der Publisher-Name der Zieldomain ("ADAC" -> adac.de, "Finanztip" -> finanztip.de)
                    const sld = host.replace(/^www\./i, "").split(".")[0]
                    if (sld && normLabel(label) === normLabel(sld)) return true

                    // ChatGPT haengt an jede Quellen-URL utm_source=chatgpt.com
                    return /[?&]utm_source=chatgpt\.com(?:&|$)/i.test(url)
                }

                const normalizeUrl = (raw) => {
                    const t = (raw || "").trim()
                    if (!isDomainOrUrl(t)) return null
                    const href = /^https?:\/\//i.test(t) ? t : `https://${t}`
                    try {
                        const u = new URL(href)
                        return { href: u.href, domain: u.hostname }
                    } catch {
                        return null
                    }
                }
                const esc = (s = "") => s.replace(/["<>]/g, (c) => ({ '"': "&quot;", "<": "&lt;", ">": "&gt;" }[c]))
                const stripTags = (str = "") => str.replace(/<[^>]+>/g, "").trim()
                const isBracketedNumber = (text = "") => /^\[\s*\d+\s*\]$/.test(text)
                const makeChip = ({ href, domain }) => {
                    const safeHref = esc(href)
                    const safeDomain = esc(domain)
                    const fav = `https://s2.googleusercontent.com/s2/favicons?domain=${encodeURIComponent(safeDomain)}`
                    return `
    <a class="la-source-chip" href="${safeHref}" target="_blank" rel="noopener noreferrer" title="${safeHref}" aria-label="${safeDomain}" data-url="${safeHref}">
        <img class="fav" src="${fav}" alt="${safeDomain}" loading="lazy" referrerpolicy="no-referrer" onerror="this.style.display='none'">
    </a>`
                }

                // Links ohne Ziel entfernen (z.B. [text]())
                html = html.replace(/<a\b[^>]*href=("|')\s*\1[^>]*>([\s\S]*?)<\/a>/gi, (match, quote, body = "") => body)

                // [[[...]]] oder [[...]]
                html = html.replace(/\[{2,3}\s*([^[\]]+?)\s*\]{2,3}/g, (m, raw) => {
                    const norm = normalizeUrl(raw)
                    return norm ? makeChip(norm) : m
                })

                // Vorhandene Links: immer href verwenden
                html = html.replace(/<a href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, (m, url, body = "") => {
                    const normUrl = normalizeUrl(url) || normalizeUrl(`https://${url}`)
                    if (!normUrl) return m

                    const visibleText = stripTags(body)
                    const shouldChip =
                        forceChipLinks ||
                        !visibleText ||
                        isBracketedNumber(visibleText) ||
                        (chipDomainLinks && (isDomainOrUrl(visibleText) || isSourceNameLabel(visibleText, url)))
                    if (shouldChip) {
                        return makeChip(normUrl)
                    }

                    const safeHref = esc(normUrl.href)
                    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${body}</a>`
                })

                return html
            },
            extractInlineLinks(html) {
                if (!html) return []
                if (typeof document === "undefined") return []

                const container = document.createElement("div")
                container.innerHTML = html

                const anchors = Array.from(container.querySelectorAll("a")).filter((a) => !a.classList.contains("la-source-chip"))

                return anchors
                    .map((anchor, idx) => {
                        const href = anchor.getAttribute("href") || ""
                        if (!href) return null

                        let domain = ""
                        try {
                            const url = new URL(href)
                            domain = (url.hostname || "").replace(/^www\./i, "")
                        } catch (e) {
                            domain = href.replace(/^https?:\/\//i, "").split("/")[0]
                        }

                        const labelText = (anchor.textContent || "").trim()
                        const label = labelText || domain || href
                        const faviconDomain = domain || ""
                        const favicon = faviconDomain ? this.faviconUrl(faviconDomain, 32) : ""

                        return {
                            id: `${domain || "link"}-${idx}-${href}`,
                            href,
                            domain,
                            label,
                            favicon
                        }
                    })
                    .filter(Boolean)
            },
            getPlatformInfo(name) {
                // Name normalisieren (z.B. für "openai" → "openai")
                const key = (name || "").toString().trim().toLowerCase()
                const platforms = {
                    openai: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "OpenAI",
                        status: this.project.platforms.includes("openai") ? "included" : "upgrade"
                    },
                    chatgpt: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "ChatGPT",
                        status: this.project.platforms.includes("chatgpt") ? "included" : "upgrade"
                    },
                    google_ai_mode: {
                        label: "AI Mode",
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        status: this.project.platforms.includes("google_ai_mode") ? "included" : "upgrade"
                    },
                    google: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AIO",
                        status: this.project.google ? "included" : "upgrade"
                    },
                    gemini: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65"><mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65"><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854  13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.447c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 1.999-5.905A1.485 1.485 0 0132.447 0z" fill="#000"/><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152  5  5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.447c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 1.999-5.905A1.485 1.485 0 0132.447 0z" fill="url(#prefix__paint0_linear_2001_67)"/></mask><g mask="url(#maskme)"><g filter="url(#prefix__filter0_f_2001_67)"><path d="M-5.859 50.734c7.498 2.663 16.116-2.33 19.249-11.152 3.133-8.821-.406-18.131-7.904-20.794-7.498-2.663-16.116 2.33-19.25 11.151-3.132 8.822.407 18.132 7.905 20.795z" fill="#FFE432"/></g><g filter="url(#prefix__filter1_f_2001_67)"><path d="M27.433 21.649c10.3 0 18.651-8.535 18.651-19.062 0-10.528-8.35-19.062-18.651-19.062S8.78-7.94 8.78 2.587c0 10.527 8.35 19.062 18.652 19.062z" fill="#FC413D"/></g><g filter="url(#prefix__filter2_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244  18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter3_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter4_f_2001_67)"><path d="M30.954 74.181c9.014-5.485 11.427-17.976 5.389-27.9-6.038-9.925-18.241-13.524-27.256-8.04-9.015 5.486-11.428 17.977-5.39 27.902 6.04 9.924 18.242 13.523 27.257 8.038z" fill="#00B95C"/></g><g filter="url(#prefix__filter5_f_2001_67)"><path d="M67.391 42.993c10.132 0 18.346-7.91 18.346-17.666 0-9.757-8.214-17.667-18.346-17.667s-18.346 7.91-18.346 17.667c0 9.757 8.214 17.666 18.346 17.666z" fill="#3186FF"/></g><g filter="url(#prefix__filter6_f_2001_67)"><path d="M-13.065 40.944c9.33 7.094 22.959 4.869 30.442-4.972 7.483-9.84 5.987-23.569-3.343-30.663C4.704-1.786-8.924.439-16.408 10.28c-7.483 9.84-5.986 23.57 3.343 30.664z" fill="#FBBC04"/></g><g filter="url(#prefix__filter7_f_2001_67)"><path d="M34.74 51.43c11.135 7.656 25.896 5.524 32.968-4.764 7.073-10.287 3.779-24.832-7.357-32.488C49.215 6.52 34.455 8.654 27.382 18.94c-7.072 10.288-3.779 24.833 7.357 32.49z" fill="#3186FF"/></g><g filter="url(#prefix__filter8_f_2001_67)"><path d="M54.984-2.336c2.833 3.852-.808 11.34-8.131 16.727-7.324 5.387-15.557 6.631-18.39 2.78-2.833-3.853.807-11.342 8.13-16.728 7.324-5.387 15.558-6.631 18.39-2.78z" fill="#749BFF"/></g><g filter="url(#prefix__filter9_f_2001_67)"><path d="M31.727 16.104C43.053 5.598 46.94-8.626 40.41-15.666c-6.53-7.04-21.006-4.232-32.332 6.274s-15.214 24.73-8.683 31.77c6.53 7.04 21.006 4.232 32.332-6.274z" fill="#FC413D"/></g><g filter="url(#prefix__filter10_f_2001_67)"><path d="M8.51 53.838c6.732 4.818 14.46 5.55 17.262 1.636 2.802-3.915-.384-10.994-7.116-15.812-6.731-4.818-14.46-5.55-17.261-1.636-2.802 3.915.383 10.994 7.115 15.812z" fill="#FFEE48"/></g></g><defs><filter id="prefix__filter0_f_2001_67" x="-19.824" y="13.152" width="39.274" height="43.217" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="2.46" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter1_f_2001_67" x="-15.001" y="-40.257" width="84.868" height="85.688" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="11.891" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter2_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter3_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter4_f_2001_67" x="-19.845" y="15.459" width="79.731" height="81.505" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter5_f_2001_67" x="29.832" y="-11.552" width="75.117" height="73.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="9.606" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter6_f_2001_67" x="-38.583" y="-16.253" width="78.135" height="78.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="8.706" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter7_f_2001_67" x="8.107" y="-5.966" width="78.877" height="77.539" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.775" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter8_f_2001_67" x="13.587" y="-18.488" width="56.272" height="51.81" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="6.957" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter9_f_2001_67" x="-15.526" y="-31.297" width="70.856" height="69.306" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.876" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter10_f_2001_67" x="-14.168" y="20.964" width="55.501" height="51.571" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.273" result="effect1_foregroundBlur_2001_67"/></filter><linearGradient id="prefix__paint0_linear_2001_67" x1="18.447" y1="43.42" x2="52.153" y2="15.004" gradientUnits="userSpaceOnUse"><stop stop-color="#4893FC"/><stop offset=".27" stop-color="#4893FC"/><stop offset=".777" stop-color="#969DFF"/><stop offset="1" stop-color="#BD99FE"/></linearGradient></defs></svg>`,
                        label: "Gemini",
                        status: this.project.platforms.includes("gemini") ? "included" : "upgrade"
                    },
                    perplexity: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" fill-rule="nonzero" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"/></svg>`,
                        label: "Perplexity",
                        status: this.project.platforms.includes("perplexity") ? "included" : "upgrade"
                    },
                    grok: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" ><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                        label: "Grok",
                        status: this.project.platforms.includes("grok") ? "included" : "upgrade"
                    },
                    microsoft_copilot: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48"><path d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z" fill="url(#prefix__paint0_radial_56201_15503)"/><path d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z" fill="url(#prefix__paint1_radial_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint2_linear_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint3_linear_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint4_radial_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint5_linear_56201_15503)"/><defs><radialGradient id="prefix__paint0_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"><stop offset=".096" stop-color="#00AEFF"/><stop offset=".773" stop-color="#2253CE"/><stop offset="1" stop-color="#0736C4"/></radialGradient><radialGradient id="prefix__paint1_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"><stop stop-color="#FFB657"/><stop offset=".634" stop-color="#FF5F3D"/><stop offset=".923" stop-color="#C02B3C"/></radialGradient><radialGradient id="prefix__paint4_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"><stop offset=".066" stop-color="#8C48FF"/><stop offset=".5" stop-color="#F2598A"/><stop offset=".896" stop-color="#FFB152"/></radialGradient><linearGradient id="prefix__paint2_linear_56201_15503" x1="12.5" y1="7.5" x2="14.788" y2="33.975" gradientUnits="userSpaceOnUse"><stop offset=".156" stop-color="#0D91E1"/><stop offset=".487" stop-color="#52B471"/><stop offset=".652" stop-color="#98BD42"/><stop offset=".937" stop-color="#FFC800"/></linearGradient><linearGradient id="prefix__paint3_linear_56201_15503" x1="14.5" y1="4" x2="15.75" y2="32.885" gradientUnits="userSpaceOnUse"><stop stop-color="#3DCBFF"/><stop offset=".247" stop-color="#0588F7" stop-opacity="0"/></linearGradient><linearGradient id="prefix__paint5_linear_56201_15503" x1="42.586" y1="13.346" x2="42.569" y2="21.215" gradientUnits="userSpaceOnUse"><stop offset=".058" stop-color="#F8ADFA"/><stop offset=".708" stop-color="#A86EDD" stop-opacity="0"/></linearGradient></defs></svg>`,
                        label: "Microsoft Copilot",
                        status: this.project.platforms.includes("microsoft_copilot") ? "included" : "upgrade"
                    },
                    amazon_rufus: {
                        logo: AMAZON_RUFUS_LOGO,
                        label: "Amazon Rufus",
                        status: this.project.platforms.includes("amazon_rufus") ? "included" : "upgrade"
                    },
                    claude: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#D77655" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.612-115.613 115.612H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#FCF2EE" fill-rule="nonzero" d="M142.27 316.619l73.655-41.326 1.238-3.589-1.238-1.996-3.589-.001-12.31-.759-42.084-1.138-36.498-1.516-35.361-1.896-8.897-1.895-8.34-10.995.859-5.484 7.482-5.03 10.717.935 23.683 1.617 35.537 2.452 25.782 1.517 38.193 3.968h6.064l.86-2.451-2.073-1.517-1.618-1.517-36.776-24.922-39.81-26.338-20.852-15.166-11.273-7.683-5.687-7.204-2.451-15.721 10.237-11.273 13.75.935 3.513.936 13.928 10.716 29.749 23.027 38.848 28.612 5.687 4.727 2.275-1.617.278-1.138-2.553-4.271-21.13-38.193-22.546-38.848-10.035-16.101-2.654-9.655-1.617-7.304-1.617-11.374l11.652-15.823 6.445-2.073 15.545 2.073 6.547 5.687 9.655 22.092 15.646 34.78 24.265 47.291 7.103 14.028 3.791 12.992 1.416 3.968 2.449-.001v-2.275l1.997-26.641 3.69-32.707 3.589-42.084 1.239-11.854 5.863-14.206 11.652-7.683 9.099 4.348 7.482 10.716-1.036 6.926-4.449 28.915-8.72 45.294-5.687 30.331h3.313l3.792-3.791 15.342-20.372 25.782-32.227 11.374-12.789 13.27-14.129 8.517-6.724 16.1-.001 11.854 17.617-5.307 18.199-16.581 21.029-13.75 17.819-19.716 26.54-12.309 21.231 1.138 1.694 2.932-.278 44.536-9.479 24.062-4.347 28.714-4.928 12.992 6.066 1.416 6.167-5.106 12.613-30.71 7.583-36.018 7.204-53.636 12.689-.657.48.758.935 24.164 2.275 10.337.556h25.301l47.114 3.514 12.309 8.139 7.381 9.959-1.238 7.583-18.957 9.655-25.579-6.066-59.702-14.205-20.474-5.106-2.83-.001v1.694l17.061 16.682 31.266 28.233 39.152 36.397 1.997 8.999-5.03 7.102-5.307-.758-34.401-25.883-13.27-11.651-30.053-25.302-1.996-.001v2.654l6.926 10.136 36.574 54.975 1.895 16.859-2.653 5.485-9.479 3.311-10.414-1.895-21.408-30.054-22.092-33.844-17.819-30.331-2.173 1.238-10.515 113.261-4.929 5.788-11.374 4.348-9.478-7.204-5.03-11.652 5.03-23.027 6.066-30.052 4.928-23.886 4.449-29.674 2.654-9.858-.177-.657-2.173.278-22.37 30.71-34.021 45.977-26.919 28.815-6.445 2.553-11.173-5.789 1.037-10.337 6.243-9.2 37.257-47.392 22.47-29.371 14.508-16.961-.101-2.451h-.859l-98.954 64.251-17.618 2.275-7.583-7.103.936-11.652 3.589-3.791 29.749-20.474-.101.102.024.101z"/></svg>`,
                        label: "Claude",
                        status: "coming soon"
                    }
                }
                return platforms[key] || { logo: null, label: name, status: null }
            }
        }
    }
</script>
<style>
    .prompt-detail-container {
        padding-top: 18px;
        padding-bottom: 32px;
    }
    .prompt-detail-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 4px 4px 14px;
        margin-bottom: 4px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    }
    .prompt-detail-header__left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }
    .prompt-detail-back {
        background: rgba(15, 23, 42, 0.04) !important;
        border-radius: 10px !important;
    }
    .prompt-detail-back:hover {
        background: rgba(15, 23, 42, 0.08) !important;
    }
    .prompt-detail-breadcrumb {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 6px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.62);
        font-weight: 500;
        letter-spacing: 0.02em;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
    }
    .prompt-detail-breadcrumb__sep {
        opacity: 0.55;
    }
    .prompt-detail-breadcrumb__crumb {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 11px;
    }
    .prompt-detail-breadcrumb__current {
        color: #0f172a;
        font-weight: 600;
        font-size: 13px;
        text-transform: none;
        letter-spacing: 0;
        max-width: 60ch;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .theme--dark .prompt-detail-header {
        border-bottom-color: rgba(255, 255, 255, 0.08);
    }
    .theme--dark .prompt-detail-breadcrumb {
        color: rgba(255, 255, 255, 0.6);
    }
    .theme--dark .prompt-detail-breadcrumb__current {
        color: #f3f4f6;
    }

    .prompt-response-card__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 16px 20px 12px;
    }
    .prompt-response-card__head-left {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }
    .prompt-response-card__icon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: rgba(var(--v-primary-rgb, 25, 118, 210), 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .prompt-response-card__title {
        font-size: 15px;
        font-weight: 600;
        color: #0f172a;
        line-height: 1.2;
    }
    .prompt-response-card__subtitle {
        font-size: 12px;
        color: rgba(15, 23, 42, 0.6);
        margin-top: 2px;
    }
    .theme--dark .prompt-response-card__title {
        color: #f3f4f6;
    }
    .theme--dark .prompt-response-card__subtitle {
        color: rgba(255, 255, 255, 0.6);
    }
    .prompt-response-card__date-input {
        max-width: 220px;
        flex-shrink: 0;
    }
    .prompt-response-card__tabs-toolbar {
        background: transparent !important;
    }

    .entity-name-text {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .entity-sentiment-badge {
        margin-left: 5px;
        padding: 2px 6px;
        border-radius: 10px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        flex-shrink: 0;
    }
    .entity-sentiment-badge.is-pos {
        background: rgba(29, 127, 89, 0.15);
        color: #1d7f59;
    }
    .entity-sentiment-badge.is-neg {
        background: rgba(179, 40, 40, 0.15);
        color: #b32828;
    }
    .entity-sentiment-badge.is-neu {
        background: rgba(107, 114, 128, 0.18);
        color: #4b5563;
    }
    .entity-sentiment-badge.is-na {
        background: rgba(156, 163, 175, 0.18);
        color: #6b7280;
    }

    .prompt-title {
        font-size: 20px;
        font-weight: 600;
    }
    .prompt-info-card {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 14px 16px 12px;
    }
    .prompt-info-title {
        margin-top: 8px;
        font-size: 15px;
        font-weight: 600;
        line-height: 1.35;
        color: #0f172a;
        display: -webkit-box;
        line-clamp: 3;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 58px;
    }
    .theme--dark .prompt-info-title {
        color: #f3f4f6;
    }
    .prompt-info-tags {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }
    .prompt-info-tag {
        font-size: 11px !important;
        height: 22px !important;
    }
    .prompt-text {
        font-size: 13px;
        line-height: 1.5;
    }
    .kpi-label {
        font-size: 11px;
        text-transform: uppercase;
        color: #777;
    }
    .kpi-value {
        font-size: 18px;
        font-weight: 600;
    }
    .kpi-small {
        font-size: 12px;
        font-weight: 500;
    }
    .card-title-row {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
    }
    .card-title {
        font-weight: 600;
        font-size: 14px;
    }
    .platform-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
        background: #1976d2;
    }
    .pointer {
        cursor: pointer;
    }

    /* Neue Styles für die Antwort-UI */
    .la-container {
        display: flex;
        flex-direction: row;
        background: #f9fafb;
        border-top: 1px solid #eef1f6;
        /* innerhalb der Card den Restplatz nutzen */
        flex: 1 1 auto;
        min-height: 0;
    }
    .la-chat-area {
        flex: 1 1 0;
        padding: 28px 28px 24px;
        min-width: 0;
        overflow-y: auto;
    }
    .prompt-response-card {
        max-height: 80vh;
    }
    /* .v-card mit im Selektor: Vuetifys ".v-card { display: block }" steckt in mehreren
       Lazy-Chunks und wird je nach Navigationsreihenfolge (z. B. direkt nach dem Login)
       NACH diesem CSS eingehängt. Bei gleicher Spezifität gewinnt dann block, die Flex-Kette
       reißt und Chat-Bereich/Sidebar lassen sich nicht mehr scrollen. */
    .v-card.prompt-response-card {
        position: relative;
        display: flex;
        flex-direction: column;
    }
    .prompt-response-card .v-card-title,
    .prompt-response-card .v-card-subtitle,
    .prompt-response_card .v-toolbar,
    .prompt-response-card .v-divider {
        flex-shrink: 0;
    }
    .prompt-response-card .la-container {
        flex: 1 1 auto;
        overflow: hidden;
    }
    .la-sidebar {
        width: 320px;
        background: #ffffff;
        border-left: 1px solid #e6e9ef;
        padding: 20px 18px;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        gap: 16px;
        overflow-y: auto;
        max-height: 100%;
    }
    .la-section-title {
        font-weight: 700;
        font-size: 0.95rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #4b5563;
        margin-bottom: 8px;
    }
    .la-source-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .la-source-chips .v-chip {
        border-color: #1976d2 !important;
        color: #1976d2 !important;
        font-weight: 600;
        letter-spacing: 0.01em;
    }

    .la-entities-table thead th {
        font-weight: 700;
        font-size: 0.9rem;
        color: #374151;
        background: #f7f9fc;
        border-bottom: 1px solid #e5e7eb;
    }
    .la-entities-table tbody td {
        font-size: 0.92rem;
        color: #1f2937;
        border-bottom: 1px solid #f1f3f6;
    }

    .la-markdown {
        font-size: 1rem;
        line-height: 1.55;
    }
    .la-markdown p {
        margin: 0.35rem 0;
    }
    .la-markdown h1,
    .la-markdown h2,
    .la-markdown h3,
    .la-markdown h4,
    .la-markdown h5,
    .la-markdown h6 {
        margin: 0.6rem 0 0.35rem;
        font-weight: 700;
        color: #0f172a;
    }
    .la-markdown ul,
    .la-markdown ol {
        padding: 0.25rem 1.2rem;
        margin: 0.2rem 0;
    }
    .la-markdown li {
        padding: 0.2rem 0;
    }
    .la-markdown code {
        font-family: Menlo, Monaco, "SFMono-Regular", Consolas, "Liberation Mono", monospace;
        background: #f1f5f9;
        padding: 2px 5px;
        border-radius: 4px;
        border: 1px solid #e2e8f0;
    }
    .la-markdown pre {
        background: #0f172a;
        color: #e2e8f0;
        padding: 10px 12px;
        border-radius: 8px;
        overflow: auto;
        font-size: 0.95rem;
    }
    .la-markdown table {
        width: 100%;
        border-collapse: collapse;
        margin: 8px 0;
    }
    .la-markdown th,
    .la-markdown td {
        padding: 8px 10px;
        border: 1px solid #e5e7eb;
        text-align: left;
        vertical-align: top;
    }
    .la-markdown thead th {
        background: #f8fafc;
        font-weight: 700;
    }
    /* Bilder aus der Antwort (z. B. Produktbilder bei ChatGPT) kommen in Originalgroesse und sprengen sonst das Layout */
    .la-markdown img {
        max-width: 100%;
        max-height: 320px;
        height: auto;
        object-fit: contain;
        border-radius: 8px;
        vertical-align: middle;
    }
    .la-markdown a.la-source-chip,
    .la-markdown a.la-source-chip:visited {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(26, 115, 232, 0.35);
        background: #fff;
        cursor: pointer;
        margin-left: 0;
        vertical-align: middle;
        position: relative;
        text-decoration: none;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .la-markdown a.la-source-chip + a.la-source-chip {
        margin-left: -8px;
    }
    .la-markdown a.la-source-chip:hover,
    .la-markdown a.la-source-chip:focus {
        transform: translateY(-1px) scale(1.08);
        box-shadow: 0 4px 10px rgba(26, 115, 232, 0.3);
        z-index: 2;
    }
    .la-markdown a.la-source-chip::after {
        content: attr(data-url);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translate(-50%, 4px);
        background: #0f172a;
        color: #fff;
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 0.72rem;
        letter-spacing: 0.01em;
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease, transform 0.15s ease;
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.35);
    }
    .la-markdown a.la-source-chip::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #0f172a;
        opacity: 0;
        transition: opacity 0.15s ease;
        pointer-events: none;
    }
    .la-markdown a.la-source-chip:hover::after,
    .la-markdown a.la-source-chip:focus::after,
    .la-markdown a.la-source-chip:hover::before,
    .la-markdown a.la-source-chip:focus::before {
        opacity: 1;
        transform: translate(-50%, 0);
    }
    .la-markdown .la-source-chip .fav {
        width: 70%;
        height: 70%;
        object-fit: contain;
        border-radius: 50%;
    }

    @media (max-width: 900px) {
        .la-container {
            flex-direction: column;
        }
        .la-sidebar {
            width: 100%;
            border-left: none;
            border-top: 1px solid #e6e9ef;
            padding: 16px 14px;
        }
    }
    .la-platform-tab {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
        letter-spacing: 0.01em;
    }
    .la-platform-logo svg {
        width: 18px;
        height: 18px;
        display: block;
    }
    .la-platform-tab-btn {
        min-height: 36px;
        padding: 0 12px;
        border-radius: 10px 10px 0px 0px !important;
        transition: all 0.18s ease;
        color: #4b5563 !important;
        text-transform: none;
        font-weight: 600;
    }
    .la-platform-tab-btn:hover {
        background: #f1f5f9 !important;
        color: #111827 !important;
    }
    .la-platform-tab-btn--active {
        background: #e8fffc !important;
        border-top: 2px solid rgb(189, 189, 189) !important;
        border-left: 2px solid rgb(189, 189, 189) !important;
        border-right: 2px solid rgb(189, 189, 189) !important;
        color: #0f172a !important;
        box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.08);
    }
    #prompt-detail div.v-slide-group__wrapper {
        margin-left: 10px;
    }
    .brand-metrics-cards-wrapper {
        margin-top: 12px;
    }
    .brand-metrics-cards-wrapper .brand-metric-card {
        position: relative;
        padding: 12px 14px 10px;
        min-height: 132px;
        border-radius: 14px !important;
        border: 1px solid rgba(15, 23, 42, 0.1) !important;
        background: #ffffff;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 8px 24px -18px rgba(15, 23, 42, 0.18);
        transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
        overflow: hidden;
    }
    .brand-metrics-cards-wrapper .brand-metric-card:hover {
        transform: translateY(-1px);
        border-color: rgba(15, 23, 42, 0.16) !important;
        box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08), 0 14px 26px -20px rgba(15, 23, 42, 0.22);
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card {
        background: #1f2328;
        border-color: rgba(255, 255, 255, 0.12) !important;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card:hover {
        box-shadow: 0 3px 9px rgba(0, 0, 0, 0.52);
    }
    .brand-metrics-cards-wrapper .brand-metric-card--up:after,
    .brand-metrics-cards-wrapper .brand-metric-card--down:after {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0.35;
        background: radial-gradient(circle at 82% 18%, rgba(255, 255, 255, 0.16), transparent 70%);
    }
    .brand-metrics-cards-wrapper .brand-metric-card--up:after {
        background: radial-gradient(circle at 82% 18%, rgba(255, 255, 255, 0.16), transparent 70%), linear-gradient(125deg, rgba(34, 142, 54, 0.12), transparent 72%);
    }
    .brand-metrics-cards-wrapper .brand-metric-card--down:after {
        background: radial-gradient(circle at 82% 18%, rgba(255, 255, 255, 0.16), transparent 70%), linear-gradient(125deg, rgba(198, 40, 40, 0.12), transparent 72%);
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card--up:after {
        background: radial-gradient(circle at 82% 20%, rgba(255, 255, 255, 0.07), transparent 65%), linear-gradient(125deg, rgba(46, 160, 67, 0.2), transparent 74%);
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card--down:after {
        background: radial-gradient(circle at 82% 20%, rgba(255, 255, 255, 0.07), transparent 65%), linear-gradient(125deg, rgba(198, 40, 40, 0.22), transparent 74%);
    }
    .brand-metrics-cards-wrapper .brand-metric-card .pc-header {
        display: flex;
        gap: 14px;
        align-items: center;
        margin-bottom: 4px;
    }
    .brand-metrics-cards-wrapper .brand-metric-card .pc-title {
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.4px;
        font-size: 0.75rem !important;
        font-weight: 500;
        letter-spacing: 0.1666666667em !important;
        /* line-height: 2rem; */
        text-transform: uppercase;
        font-family: "Roboto", sans-serif !important;
    }
    .pc-title-wrap {
        display: flex;
        flex-direction: column;
        line-height: 1.05;
    }
    .pc-status {
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        opacity: 0.8;
        margin-top: 1px;
    }
    .la-entities-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .la-entity-row {
        display: flex;
        align-items: center;
        padding: 8px 10px;
        border-radius: 12px;
        background: #f1f5f9;
        color: #1f2937;
        text-decoration: none;
        transition: background 0.2s, transform 0.2s;
    }

    .la-entity-row:hover {
        background: #eaf3ff;
        transform: translateY(-1px);
    }

    .entity-dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        margin-right: 10px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        flex: 0 0 auto;
    }
    .theme--dark .entity-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }

    .entity-avatar {
        width: 32px;
        height: 32px;
        border-radius: 999px;
        background: #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
        font-weight: 600;
        color: #4b5563;
        flex-shrink: 0;
    }

    .entity-meta {
        display: flex;
        flex-direction: column;
        margin-left: 10px;
        overflow: hidden;
    }

    .entity-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: #111827;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .entity-sub {
        font-size: 0.75rem;
        color: #6b7280;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* optional Dark-Mode-Anpassungen */
    .theme--dark .la-entity-row {
        background: #1f2933;
        color: #e5e7eb;
    }
    .theme--dark .la-entity-row:hover {
        background: #27323d;
    }
    .theme--dark .entity-avatar {
        background: #374151;
        color: #e5e7eb;
    }
    .brand-metrics-cards-wrapper .brand-metric-card .pc-logo {
        width: 48px;
        height: 48px;
        border-radius: 14px;
        background: linear-gradient(135deg, #f0f4f8, #ffffff);
        border: 1px solid rgba(0, 0, 0, 0.06);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08), 0 0 0 5px rgba(255, 255, 255, 0.9);
        overflow: hidden;
        position: relative;
        transition: transform 0.35s;
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card .pc-logo {
        background: linear-gradient(135deg, #2a2d31, #1d1f22);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.55), 0 0 0 5px rgba(0, 0, 0, 0.35);
    }
    .brand-metrics-cards-wrapper .brand-metric-card:hover .pc-logo {
        transform: translateY(-1px) scale(1.03);
    }
    .brand-metrics-cards-wrapper .brand-metric-card .pc-body {
        display: flex;
        justify-content: space-between;
        align-items: stretch;
        gap: 12px;
        margin-top: 2px;
    }
    .pc-metric-label,
    .pc-diff-label {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.55px;
        opacity: 0.8;
        margin-bottom: 2px;
    }
    .pc-value {
        font-size: 26px;
        letter-spacing: -0.5px;
        font-variant-numeric: tabular-nums;
        line-height: 1.05;
        display: flex;
        gap: 4px;
    }
    .pc-side {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        min-width: 84px;
        gap: 4px;
    }
    .pc-sentiment-meter {
        margin-top: 8px;
        width: 100%;
    }
    .pc-sentiment-meter--compact {
        margin-top: 0;
    }
    .pc-sentiment-meter__bar {
        position: relative;
        height: 10px;
        border-radius: 999px;
        overflow: hidden;
        background: linear-gradient(90deg, #c62828 0%, #e5e7eb 50%, #1d7f2b 100%);
    }
    .theme--dark .pc-sentiment-meter__bar {
        border-color: rgb(183, 183, 183);
    }
    .pc-sentiment-meter__zero {
        position: absolute;
        left: 50%;
        top: 0;
        width: 2px;
        height: 100%;
        background: rgba(0, 0, 0, 0.25);
    }
    .theme--dark .pc-sentiment-meter__zero {
        background: rgba(255, 255, 255, 0.22);
    }
    .pc-sentiment-meter__marker {
        position: absolute;
        top: 50%;
        width: 12px;
        height: 12px;
        transform: translateY(-50%);
        border-radius: 999px;
        background: #ffffff;
        border: 2px solid rgba(15, 23, 42, 0.85);
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    }
    .theme--dark .pc-sentiment-meter__marker {
        background: #0f172a;
        border-color: rgba(255, 255, 255, 0.7);
    }
    .pc-sentiment-meter__legend {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        font-weight: 700;
        opacity: 0.7;
        margin-top: 4px;
    }
    .pc-delta-badge {
        font-size: 18px;
        padding: 5px 10px 4px 8px;
        border-radius: 18px;
        background: #eef2f5;
        color: #3a4751;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        line-height: 1.05;
        font-variant-numeric: tabular-nums;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: background 0.25s, color 0.25s;
    }
    .theme--dark .pc-delta-badge {
        background: #2a3034;
        color: rgba(255, 255, 255, 0.75);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.65);
    }
    .pc-delta-badge.up {
        background: color-mix(in srgb, #1d7f2b 100%, transparent);
        color: #ffffff;
    }
    .pc-delta-badge.down {
        background: color-mix(in srgb, #c62828 100%, transparent);
        color: #ffffff;
    }
    .theme--dark .pc-delta-badge.up {
        background: color-mix(in srgb, #2ea043 32%, transparent);
        color: #49d06a;
    }
    .theme--dark .pc-delta-badge.down {
        background: color-mix(in srgb, #c62828 30%, transparent);
        color: #ff6a6a;
    }
    .pc-delta-badge.na {
        opacity: 0.45;
    }

    .la-markdown a {
        color: #1a73e8;
        text-decoration: underline;
        font-weight: 500;
    }
    .la-markdown a:hover,
    .la-markdown a:focus,
    .la-markdown a:active {
        color: #0c47a1;
    }

    .la-sources-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .la-source-row {
        display: flex;
        align-items: center;
        padding: 8px 10px;
        border-radius: 12px;
        background: #f1f5f9;
        color: #1f2937;
        text-decoration: none;
        transition: background 0.2s, transform 0.2s;
    }
    .la-source-row:hover {
        background: #eaf3ff;
        transform: translateY(-1px);
    }
    .fav_wrapper {
        min-width: 32px;
        height: 32px;
        border-radius: 8px;
        background: #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
    }
    .fav_wrapper.fail {
        background: #fee2e2;
    }
    .fav {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .domain-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        font-weight: 500;
        color: #9ca3af;
    }
    .domain-meta {
        display: flex;
        flex-direction: column;
        margin-left: 10px;
        overflow: hidden;
    }
    .domain-text {
        font-size: 0.875rem;
        font-weight: 500;
        color: #111827;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .domain-path {
        font-size: 0.75rem;
        font-weight: 400;
        color: #6b7280;
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .pc-platform-icons {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 2px;
    }

    .pc-platform-icon {
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        transition: filter 0.2s, opacity 0.2s, transform 0.2s;
    }

    .theme--dark .pc-platform-icon {
        background: #1f2933;
    }

    .pc-platform-icon-logo svg {
        width: 18px;
        height: 18px;
        display: block;
    }

    .pc-platform-icon-fallback {
        font-size: 0.75rem;
        font-weight: 600;
        color: #4b5563;
    }

    .pc-platform-icon.is-hidden {
        filter: grayscale(1);
        opacity: 0.35;
    }

    .pc-platform-icon.is-visible {
        filter: none;
        opacity: 1;
    }

    .pc-metric-sub {
        margin-top: 4px;
        font-size: 11px;
        opacity: 0.8;
    }

    .la-entity-highlight {
        color: var(--v-primary-base);
        border: 1.5px solid var(--v-primary-base);
        border-radius: 4px;
        padding: 1px 4px;
        background: rgba(44, 123, 229, 0.07);
        font-weight: 600;
        margin: 0 1px;
        box-shadow: none;
    }

    .la-entity-highlight.entity-organization {
        color: #1976d2;
        border-color: #1976d2;
        background: rgba(25, 118, 210, 0.07);
    }
    .la-entity-highlight.entity-person {
        color: #43a047;
        border-color: #43a047;
        background: rgba(67, 160, 71, 0.07);
    }
    .la-entity-highlight.entity-location {
        color: #0288d1;
        border-color: #0288d1;
        background: rgba(2, 136, 209, 0.07);
    }
    .la-entity-highlight.entity-product {
        color: #ffa000;
        border-color: #ffa000;
        background: rgba(255, 160, 0, 0.07);
    }
    .la-entity-highlight.entity-event {
        color: #e53935;
        border-color: #e53935;
        background: rgba(229, 57, 53, 0.07);
    }
    .la-entity-highlight.entity-other {
        color: #757575;
        border-color: #757575;
        background: rgba(117, 117, 117, 0.07);
    }
    .la-entity-highlight.entity-consumer_good {
        color: #fbc02d;
        border-color: #fbc02d;
        background: rgba(251, 192, 45, 0.07);
    }

    .la-entity-chip-group {
        margin-bottom: 6px;
    }
</style>

<style scoped>
    /* Shopping-Karussell in der Response-Ansicht */
    .la-queries {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin: 10px 0 4px;
    }
    .la-queries--fanout {
        margin-top: 14px;
        padding-top: 10px;
        border-top: 1px dashed rgba(0, 0, 0, 0.08);
    }
    .la-queries__label {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--v-primary-base);
        margin-right: 4px;
        white-space: nowrap;
        cursor: default;
    }
    .la-queries__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
    }
    .la-query-chip {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 3px 10px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 999px;
        background: rgba(0, 0, 0, 0.03);
        font-size: 12px;
        line-height: 1.3;
        color: rgba(0, 0, 0, 0.72);
        word-break: break-word;
    }
    .la-query-chip--toggle {
        cursor: pointer;
        border-style: dashed;
        background: transparent;
        color: var(--v-primary-base);
        font-weight: 600;
    }
    .la-query-chip--toggle:hover {
        background: rgba(0, 0, 0, 0.04);
    }
    .theme--dark .la-query-chip {
        border-color: rgba(255, 255, 255, 0.16);
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.8);
    }
    .theme--dark .la-queries--fanout {
        border-top-color: rgba(255, 255, 255, 0.12);
    }
    .la-shopping {
        margin: 10px 0 14px;
        padding: 10px 12px;
        border: 1px solid rgba(0, 160, 137, 0.25);
        border-radius: 10px;
        background: rgba(0, 160, 137, 0.04);
    }
    .la-shopping__label {
        display: flex;
        align-items: center;
        font-size: 0.72rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #00806e;
        margin-bottom: 8px;
    }
    .la-shopping__scroller {
        display: flex;
        gap: 10px;
        overflow-x: auto;
        padding-bottom: 4px;
    }
    .la-shopping-card {
        position: relative;
        flex: 0 0 150px;
        width: 150px;
        background: #fff;
        border: 1px solid #e5e9ef;
        border-radius: 10px;
        padding: 8px;
    }
    .la-shopping-card__rank {
        position: absolute;
        top: 6px;
        left: 6px;
        font-size: 0.62rem;
        font-weight: 700;
        background: #eef1f5;
        color: #5b6472;
        border-radius: 6px;
        padding: 1px 5px;
        z-index: 1;
    }
    .la-shopping-card__rank.is-buybox {
        background: #fff3d6;
        color: #a7700a;
    }
    .la-shopping-card__img-wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 84px;
        margin-bottom: 6px;
    }
    .la-shopping-card__img-wrap img {
        max-height: 84px;
        max-width: 100%;
        object-fit: contain;
        border-radius: 6px;
    }
    .la-shopping-card__title {
        font-size: 0.72rem;
        font-weight: 600;
        line-height: 1.25;
        max-height: 2.5em;
        overflow: hidden;
        margin-bottom: 2px;
    }
    .la-shopping-card__merchant {
        font-size: 0.68rem;
        color: #8a94a6;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .la-shopping-card__more {
        color: #b0b8c4;
    }
    .la-shopping-card__meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 4px;
        margin-top: 4px;
    }
    .la-shopping-card__price {
        font-size: 0.74rem;
        font-weight: 700;
    }
    .la-shopping-card__rating {
        font-size: 0.68rem;
        color: #5b6472;
        white-space: nowrap;
    }

    /* google_ai_mode: Web-Ausschnitte als Ergebnisliste statt als ein Fliesstext-Absatz */
    .la-aimode-intro {
        margin-bottom: 10px;
    }
    .la-aimode-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .la-aimode-card {
        display: block;
        background: #fff;
        border: 1px solid #e5e9ef;
        border-radius: 10px;
        padding: 10px 12px;
        text-decoration: none;
        color: inherit;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .la-aimode-card:hover {
        border-color: #c7d0dc;
        box-shadow: 0 1px 4px rgba(31, 41, 55, 0.06);
    }
    .la-aimode-card__head {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
    }
    .la-aimode-card__img {
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        border-radius: 4px;
        object-fit: contain;
    }
    .la-aimode-card__ident {
        min-width: 0;
    }
    .la-aimode-card__name {
        font-size: 0.78rem;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .la-aimode-card__domain {
        font-size: 0.68rem;
        color: #8a94a6;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .la-aimode-card__text {
        font-size: 0.8rem;
        line-height: 1.45;
        color: #3c4655;
    }

    /* Perplexity: Quellen-Marker ("breuninger +4") aus dem Klartext als Chip */
    .la-ppx-marker {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
    }
    .la-ppx-marker__chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        max-width: 100%;
        padding: 3px 9px 3px 5px;
        border: 1px solid #e5e9ef;
        border-radius: 999px;
        background: #fff;
        font-size: 0.72rem;
        color: #3c4655;
        text-decoration: none;
        transition: border-color 0.15s ease;
    }
    .la-ppx-marker__chip:hover {
        border-color: #c7d0dc;
    }
    .la-ppx-marker__chip .fav {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        object-fit: contain;
    }
    .la-ppx-marker__chip.is-plain {
        color: #5b6472;
    }
    .la-ppx-marker__more {
        font-size: 0.7rem;
        font-weight: 600;
        color: #5b6472;
        background: #eef1f5;
        border-radius: 999px;
        padding: 2px 7px;
    }
    .la-shopping-card__votes {
        color: #b0b8c4;
    }
    .la-shopping-card__query {
        margin-top: 4px;
        font-size: 0.64rem;
        font-style: italic;
        color: #8a94a6;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: help;
    }
    .theme--dark .la-shopping-card {
        background: #23272e;
        border-color: #3a3f47;
    }
</style>
